import NumberSeparators from 'utils/personalizer/NumberSeparators';
import NumberFormats from 'utils/personalizer/NumberFormats';
import DateFormats from 'utils/personalizer/DateFormats';
import MeasurementSystems from 'utils/personalizer/MeasurementSystems';
import WeightUnitKeys from 'utils/personalizer/WeightUnitKeys';
import ConversionFactors from 'utils/personalizer/ConversionFactors';
import SpeedTypes from 'utils/personalizer/SpeedTypes';
import CadenceSensorTypes from 'utils/personalizer/CadenceSensorTypes';
import Conversion from 'utils/Conversion';
import Moment from 'moment';
import DateFormatter from 'utils/date/DateFormatter';
import FluidTypes from 'utils/hydration/FluidTypes';
import TrainingStatusUtil from 'utils/TrainingStatusUtil';
import ActivitiesUtil from 'utils/activity/ActivitiesUtil';
import formatNumber from 'utils/NumberFormatter';
import { isNull, isUndefined } from 'utils/nodash';
import DistanceUnits from 'utils/personalizer/DistanceUnits';
import ActivityTypeMapper from 'utils/ActivityTypeMapper';

// Enums broken out to their own files for ease of testing

/**
 * Class that is responsible for personalization number values. Numbers may be personalized based
 * on measurement system preference, activity type, and locale.  This class utilizes a JQuery number
 * formatting plugin to handle many locales for formatting long number with decimals (comma, period handling, etc).
 */
var Personalizer = {
    // Enum that represents the Measurement systems supported.
    MeasurementSystems: MeasurementSystems,

    // Enum that represents the Date formats supported.
    DateFormats: DateFormats,

    // state variables
    measurementSystem: MeasurementSystems.STATUTE_US,
    golfMeasurementSystem: MeasurementSystems.STATUTE_US,
    dateFormat: DateFormats.MMDDYYYY,
    speedType: SpeedTypes.Speed,
    cadenceSensorType: 'Unknown',
    numberSeparator: NumberSeparators.DECIMAL_PERIOD,

    /**
     * Sets the measurement system to the given value.
     *
     * @param measurementSystem the new MeasurementSystem.
     */
    setMeasurementSystem: function(measurementSystem) {
        this.measurementSystem = measurementSystem;
    },

    /**
     * Sets the golf measurement system to the given value.
     *
     * @param golfMeasurementSystem the new GolfMeasurementSystem.
     */
    setGolfMeasurementSystem: function(golfMeasurementSystem) {
        this.golfMeasurementSystem = golfMeasurementSystem;
    },

    /**
     * Sets the date format to the given value.
     *
     * @param dateFormat the new DateFormat.
     */
    setDateFormat: function(dateFormat) {
        this.dateFormat = dateFormat;
    },

    /**
     * Sets the time format to the given value.
     *
     * @param timeFormat the new TimeFormat.
     */
    setTimeFormat: function(timeFormat) {
        this.timeFormat = timeFormat;
    },

    /**
     * Sets the separator to be used when formatting numbers. Some cultures use commas to group digits into
     * threes, others use decimals.
     * @param separatorName: This is the name of the separator. Not the value.
     */
    setNumberSeparator: function(separatorName) {
        this.numberSeparator = NumberSeparators[separatorName];
    },

    /**
     * Gets the current number separator (which is a really a locale that corresponds to it.
     */
    getNumberSeparator: function() {
        return this.numberSeparator;
    },

    getMeasurementSystem: function() {
        return this.measurementSystem;
    },

    getGolfMeasurementSystem: function() {
        return this.golfMeasurementSystem;
    },

    /**
     * Sets the speed type to use for speed/pace labels.
     *
     * @param newSpeedType the SpeedType to use.
     */
    setSpeedType: function(newSpeedType) {
        this.speedType = newSpeedType;
    },

    /**
     * Personalizes height based on measurement system used.
     *
     * @param value the value to personalize.
     * @param optionalNumberFormat is an override for the default number format
     */
    personalizeHeight: function(value, optionalNumberFormat) {
        let numberFormat = optionalNumberFormat || NumberFormats.PRECISION1_NOZERO;

        return formatNumber(value, this.numberSeparator, numberFormat);
    },

    /**
     * Convert the given height value in centimeter to the preferred measurement system
     * and format.
     *
     * @param value: The value to be converted (in centimeter).
     */
    convertHeight: function(value) {
        if (isNaN(value)) {
            throw 'value must be in centimeter';
        }

        var converted = {};
        converted.type = this.measurementSystem;
        converted.cm = value;

        var realFeet = value * this.ConversionFactors.CmToFeet;
        converted.feet = Math.floor(realFeet);
        converted.inches = Math.round((realFeet - converted.feet) * 12);

        var feetFix = converted.inches === 12 ? 1 : 0;
        converted.inches = converted.inches === 12 ? 0 : converted.inches;
        converted.feet += feetFix;

        return converted;
    },

    /**
     * Given the personalized (but not formatted) value, converts it to cm.
     * @param value the value to convert.
     */
    convertHeightToCm: function(value) {
        if (this.measurementSystem === this.MeasurementSystems.METRIC) {
            return value;
        } else {
            return value / this.ConversionFactors.CmToFeet;
        }
    },

    /**
     * Given a distance in the user's preferred measurement system,
     * converts it to the correct value in meters, but
     * without formatting it.
     *
     * Note: For Statute_US the value is converted to a string and only 2 decimal places are returned
     *
     * @param {Number} value the value to convert to meters of elevation
     * @returns {Number}
     */
    convertFeetToMeter: function(value) {
        if (!value) {
            return 0;
        }
        //Statute_UK uses metres for elevation
        if (this.measurementSystem === this.MeasurementSystems.STATUTE_US) {
            return (value * this.ConversionFactors.FeetToMeters).toFixed(2);
        } else {
            return value;
        }
    },

    convertElevationToMeter: function(value) {
        if (!value) {
            return 0;
        }
        //Statute_UK uses metres for elevation
        if (this.measurementSystem === this.MeasurementSystems.STATUTE_US) {
            return (value * this.ConversionFactors.FeetToMeters).toFixed(2);
        } else {
            return value;
        }
    },

    /**
     * Given an elevation in meters, converts it to the correct value for the current user's preference, but
     * without formatting it.
     *
     * @param value the value to convert.
     */
    convertElevation: function(value) {
        return this.convertMetersFt(value);
    },

    /**
     * This function takes an elevation value from a converted value to its display value.
     * @param value
     * @param decimals - If true, formats the number with decimals
     */
    formatElevation: function(value, decimals) {
        return this.formatMetersFt(value, decimals);
    },

    /**
     * Personalizes the given elevation value in meters to the preferred measurement system
     * and format. For example, converts 334 (meters) to 1,096 (feet). Note that the label
     * is not returned.
     *
     * @param value: The value to be formatted. Should be an int representing meters.
     * @param decimals Format with decimals
     */
    personalizeElevation: function(value, decimals) {
        if (isNull(value) || isUndefined(value)) {
            return '--';
        }
        return this.personalizeMetersFt(value, decimals);
    },

    /**
     * Personalizes the given surf wave distance value in meters to the preferred measurement system
     * and format. For example, converts 334 (meters) to 1,096 (feet). Note that the label
     * is not returned.
     *
     * @param value: The value to be formatted. Should be an int representing meters.
     */
    personalizeWaveDistance: function(value) {
        if (isNull(value) || isUndefined(value)) {
            return '--';
        }
        return this.personalizeDistanceMetersFt(value);
    },

    /**
     * Formats the provided distance to 2 decimal places by default, using the number separator for the account
     *
     * @param distance {Object}
     * @param format {String} - If provided, the default format is overridden
     */
    personalizeDistanceValue: function(distance, format) {
        let formatObj = format || NumberFormats.PRECISION2;

        return formatNumber(distance, this.numberSeparator, formatObj);
    },

    /**
     * Personalizes distance values based on measurement system used and locale to 1 decimal place.
     *
     * @param value the value to personalize.
     * @param activityType the activity type, used to determine what units to convert to, can be null(will convert to km/mi)
     */
    personalizeDistancePrecision1: function(value, activityType) {
        var distance = this.convertDistance(value, activityType);
        if (this.isLapSwimmingActivity(activityType)) {
            return this.formatWholeNumber(distance);
        } else {
            return formatNumber(distance, this.numberSeparator, NumberFormats.PRECISION1);
        }
    },

    /**
     * Personalizes distance values based on measurement system used and locale to 2 decimal places.
     *
     * @param value the value to personalize.
     * @param activityType the activity type, used to determine what units to convert to, can be null(will convert to km/mi)
     */
    personalizeDistancePrecision2: function(value, activityType) {
        var distance = this.convertDistance(value, activityType);
        if (this.isLapSwimmingActivity(activityType)) {
            return this.formatWholeNumber(distance);
        } else {
            return formatNumber(distance, this.numberSeparator, NumberFormats.PRECISION2);
        }
    },

    /**
     * Personalizes distance values based on measurement system used and locale.
     *
     * @param value the value to personalize.
     * @param activityType - String representing the activity type
     * @param poolUnit - Object containing the unitKey for the pool length unit
     */
    personalizeDistance: function(value, activityType, poolUnit) {
        if (isNull(value) || isUndefined(value) || Number.isNaN(value)) {
            return '--';
        }
        var distance = this.convertDistance(value, activityType, poolUnit ? poolUnit.unitKey : null);
        return this.formatDistance(distance, activityType);
    },

    /**
     * Formats an already converted distance value.
     * @param distance The value to format.
     * @param activityType the activity type.
     * @returns {*}
     */
    formatDistance: function(distance, activityType) {
        if (this.displayDistanceAsWholeNumber(activityType)) {
            return this.formatWholeNumber(distance);
        }

        return formatNumber(distance, this.numberSeparator, NumberFormats.PRECISION2);
    },

    /**
     * Personalizes the given value in meters to the equivalent distance using the user's preferences. This ignores
     * pool length so shouldn't be used for activities but okay for totals and reports.
     * @param value the value to personalize.
     * @param measurementSystem the measurement system to use
     */
    personalizeSwimDistance: function(value, measurementSystem) {
        var measurementSystemToUse = measurementSystem || this.measurementSystem;

        if (measurementSystemToUse === this.MeasurementSystems.METRIC) {
            return this.formatWholeNumber(value);
        } else {
            return this.formatWholeNumber(value * this.ConversionFactors.MetersToYards);
        }
    },

    /**
     * Formats the pool distance which is depedent on pool size and number of lengths. Only for pool swimming activity details page.
     * @param value
     */
    formatSwimDistance: function(value) {
        return this.formatWholeNumber(value);
    },

    /**
     * Personalizes the display of pool length.
     * @param value
     */
    personalizePoolLength: function(value) {
        return this.formatWholeNumber(value);
    },

    /**
     * Personalizes steps values, inserting commas or periods based on the users locale.
     * @param value
     */
    personalizeSteps: function(value) {
        return this.formatWholeNumber(value);
    },

    /**
     * Computes a percentage when given a value and total.  Optional allowOverflow will allow %s > 100%
     * @param value - The numerator, eg user has 1500 steps for the day
     * @param total - The denominator, eg the users goal is 6000 for the day(result of example is 25)
     * @param {optional} allowOverflow - If true, allows % to exceed 100, eg user has 12000 steps against a goal of 10000 would return 100 if false and 120 if true
     */
    personalizePercentComplete: function(value, total, allowOverflow) {
        var actualPercent;
        var percentComplete;

        if (total) {
            actualPercent = Math.floor((value / total) * 100);
        } else {
            actualPercent = 100;
        }

        percentComplete = allowOverflow ? actualPercent : +Math.min(actualPercent, 100);

        if (isNaN(percentComplete)) {
            percentComplete = 0;
        }

        return percentComplete;
    },

    /** Converts the given distance value in meters to the current measurement system, but without formatting
     * the number (adding commas, etc).
     *
     * @param value the value to convert.
     * @param optional activity type parameter, required by some activity types
     */
    convertDistance: function(value, activityType, poolUnit) {
        var isLapSwimmingActivity = this.isLapSwimmingActivity(activityType); // null or undefined is okay
        var isOpenWaterSwimming = this.isOpenWaterSwimmingActivity(activityType);
        const isNauticalActivity = this.isNauticalActivity(activityType);
        const isNauticalActivityException = this.isNauticalActivityException(activityType);

        if (isLapSwimmingActivity) {
            let measurementSystem;
            if (poolUnit) {
                measurementSystem = this.poolUnitToMeasurementSystem(poolUnit);
            } else {
                measurementSystem = this.inferPoolMeasurementSystemFromDistance(value);
            }
            if (measurementSystem == this.MeasurementSystems.METRIC) {
                return value;
            } else {
                return value * this.ConversionFactors.MetersToYards;
            }
        } else if (isOpenWaterSwimming) {
            if (this.measurementSystem == this.MeasurementSystems.METRIC) {
                return value;
            } else {
                return value * this.ConversionFactors.MetersToYards;
            }
        } else if (isNauticalActivity && !isNauticalActivityException) {
            // exception for Windsurfing Activity Types to show normal distance measurements as required
            return value * this.ConversionFactors.MetersToNauticalMiles;
        } else if (this.isDisplayInMeter(activityType)) {
            return value;
        } else {
            if (this.measurementSystem == this.MeasurementSystems.METRIC) {
                return value * this.ConversionFactors.MetersToKm;
            } else {
                return value * this.ConversionFactors.MetersToMiles;
            }
        }
    },

    /**
     * Convert distance to centimeters. This is a utility function for use with models
     * that store normalized distance as centimeters.
     */
    convertDistanceToCm: function(value, unitKey) {
        switch (unitKey) {
            case DistanceUnits.meter:
                return value / this.ConversionFactors.CmToMeters;
            case DistanceUnits.kilometer:
                return value * this.ConversionFactors.KmToCm;
            case DistanceUnits.yard:
                return (value / this.ConversionFactors.MetersToYards) * 100;
            case DistanceUnits.mile:
                return value / this.ConversionFactors.CmToMiles;
            case DistanceUnits.inch:
                return value / this.ConversionFactors.CmToInches;
            case DistanceUnits.nauticalMile:
                return value / this.ConversionFactors.CmToNauticalMiles;
            default:
                throw 'Unknown unit key: ' + unitKey;
        }
    },

    // Takes a pace in meters / sec and returns the pace in sec / mile or sec / km
    convertIntensityTargetPace: function(pace) {
        let value = pace;

        if (this.measurementSystem === this.MeasurementSystems.METRIC) {
            value = this.ConversionFactors.KmToMeters / value;
        } else {
            value = this.ConversionFactors.MilesToMeters / value;
        }

        return Math.round(value);
    },

    revertDistanceToCm: function(value, unitKey) {
        switch (unitKey) {
            case DistanceUnits.inch:
                return value * this.ConversionFactors.CmToInches;
            case DistanceUnits.meter:
                return value * this.ConversionFactors.CmToMeters;
            case DistanceUnits.yard:
                return value * this.ConversionFactors.CmToMeters * this.ConversionFactors.MetersToYards;
            case DistanceUnits.nauticalMile:
                return value * this.ConversionFactors.CmToNauticalMiles;
            default:
                throw 'Unknown unit key: ' + unitKey;
        }
    },

    /***
     * This is a function that converts a converted value back to the storage value using the current measurement
     * system. In other words, it reverses personalization.
     * @param value
     */
    revertDistance: function(value, activityType, poolUnit) {
        var isLapSwimmingActivity = this.isLapSwimmingActivity(activityType); // null or undefined is okay
        var isOpenWaterSwimming = this.isOpenWaterSwimmingActivity(activityType);
        const isNauticalActivity = this.isNauticalActivity(activityType);
        const isNauticalActivityException = this.isNauticalActivityException(activityType);

        if (isLapSwimmingActivity) {
            let measurementSystem;
            if (poolUnit) {
                measurementSystem = this.poolUnitToMeasurementSystem(poolUnit);
            } else {
                measurementSystem = this.inferPoolMeasurementSystemFromDistance(value);
            }
            if (measurementSystem == this.MeasurementSystems.METRIC) {
                return value;
            } else {
                return value * this.ConversionFactors.YardsToMeters;
            }
        } else if (isOpenWaterSwimming) {
            if (this.measurementSystem == this.MeasurementSystems.METRIC) {
                return value;
            } else {
                return value * this.ConversionFactors.YardsToMeters;
            }
        } else if (this.isDisplayInMeter(activityType)) {
            return value;
        } else if (isNauticalActivity && !isNauticalActivityException) {
            return value * this.ConversionFactors.NauticalMilesToMeters;
        } else {
            if (this.measurementSystem == this.MeasurementSystems.METRIC) {
                return value * this.ConversionFactors.KmToMeters;
            } else {
                return value * this.ConversionFactors.MilesToMeters;
            }
        }
    },

    // Takes a pace in sec / mile or sec / km and converts it to meters / sec
    revertIntensityTargetPace: function(pace) {
        let value = pace;

        if (this.measurementSystem === this.MeasurementSystems.METRIC) {
            value = value * this.ConversionFactors.MetersToKm;
        } else {
            value = value * this.ConversionFactors.MetersToMiles;
        }

        value = 1 / value;

        return value;
    },

    /**
     * Given a value in distance/sec, converts it to the equivilent distance in minutes/distance (pace).
     *
     * @param value the value to convert.
     */
    personalizePace: function(value, displayInMinutes, activityTypeKey, getZeroIfOverHour = true) {
        var isRowingActivity = this.isRowingActivity(activityTypeKey),
            speed = isRowingActivity ? value : this.convertSpeed(value, null, activityTypeKey);

        if (speed == 0.0) {
            return 0;
        }

        if (isRowingActivity) {
            //call rowing pace function
            return this.personalizeRowingPace(speed);
        }

        var seconds = Math.round(3600 / speed);
        var hours = Math.floor(seconds / 3600);
        var minutes = Math.floor((seconds - hours * 3600) / 60);
        seconds = Math.floor(seconds - hours * 3600 - minutes * 60);

        //if anything over 1 hours, return zero - Jira 7859
        if (
            getZeroIfOverHour &&
            (hours > 0 || (hours === 0 && minutes === 60) || (hours === 0 && minutes === 59 && seconds === 60))
        ) {
            return 0;
        }

        var paceString = '';

        if (displayInMinutes) {
            paceString =
                minutes +
                hours * 60 +
                ':' +
                formatNumber(seconds, this.numberSeparator, NumberFormats.NO_DECIMAL_PAD_2_ZEROS);
        } else {
            paceString = minutes + ':' + formatNumber(seconds, NumberFormats.NO_DECIMAL_PAD_2_ZEROS);
            if (hours > 0) {
                paceString = hours + ':' + paceString;
            }
        }

        return paceString;
    },

    personalizePaceInSeconds: function(value, activityTypeKey) {
        var isRowingActivity = this.isRowingActivity(activityTypeKey),
            speed = isRowingActivity ? value : this.convertSpeed(value, null, activityTypeKey);

        if (speed == 0.0) {
            return 0;
        }

        if (isRowingActivity) {
            //call rowing pace function
            return this.personalizeRowingPace(speed);
        }

        var seconds = Math.round(3600 / speed);

        return seconds;
    },

    poolUnitKeyToMeasurementSystem: function(poolUnit) {
        return this.poolUnitToMeasurementSystem(poolUnit) || Personalizer.MeasurementSystems.STATUTE_US;
    },

    /**
     * Converts a given (m/s) value to the specified measurement system in 100meters or 100yards a minute. The minutes
     * are represented in a fractional form.
     * @param value
     * @param measurementSystem
     * @returns {number}
     */
    convertFractionalSwimPace: function(value, measurementSystem) {
        measurementSystem = measurementSystem || this.measurementSystem;

        var speed = this.convertSpeed(value, measurementSystem);

        if (!speed) {
            return 60;
        }

        var conversionFactor = this.ConversionFactors.MilesToYards;
        if (measurementSystem == this.MeasurementSystems.METRIC) {
            conversionFactor = this.ConversionFactors.KmToMeters;
        }

        return (1 / ((speed / 60) * conversionFactor)) * 100;
    },

    /**
     * Given a measurment system, converts the value in meters/sec to 100 meters/hour without formatting it.
     * @param value the value to convert
     * @param measurementSystem the measurement system
     * @returns the converted speed.
     */
    convertSwimSpeed: function(value, measurementSystem) {
        var speed;
        if (measurementSystem == this.MeasurementSystems.METRIC) {
            speed = value * this.ConversionFactors.MeterSecTo100MeterHour;
        } else {
            speed = value * this.ConversionFactors.MeterSecTo100YardHour;
        }

        return speed;
    },

    /**
     * Convert the value in mm/sec to sec/100 m.
     * @param value the value to convert
     * @returns the critical swim speed in sec/100 m
     */
    convertCriticalSwimSpeed: function(value) {
        return Math.round((this.ConversionFactors.MillimetersToMeters * 100) / value);
    },

    /**
     * Given a measurment system, converts the value in meters/sec to feet/sec without formatting it.
     * @param value the value to convert
     * @param measurementSystem the measurement system
     * @returns the converted velocity (speed).
     */
    convertVelocity: function(value, measurementSystem) {
        var speed;
        if (measurementSystem == this.MeasurementSystems.METRIC) {
            speed = value;
        } else {
            speed = value * this.ConversionFactors.MetersToFeet;
        }

        return speed;
    },

    /**
     * Takes a value in minutes represented as a fraction and converts it to a display value in minutes and seconds
     */
    formatPace: function(value) {
        if (!value) {
            return '0:00';
        }

        var seconds = Math.round(value * 60);
        var hours = Math.floor(seconds / 3600);
        var minutes = Math.floor((seconds - hours * 3600) / 60);
        seconds = Math.floor(seconds - hours * 3600 - minutes * 60);

        var hoursDisplay = hours >= 1 ? hours + ':' : '';

        if (hoursDisplay) {
            return (
                hoursDisplay +
                formatNumber(minutes, NumberFormats.NO_DECIMAL_PAD_2_ZEROS) +
                ':' +
                formatNumber(minutes, NumberFormats.NO_DECIMAL_PAD_2_ZEROS)
            );
        }
        return hoursDisplay + minutes + ':' + formatNumber(seconds, NumberFormats.NO_DECIMAL_PAD_2_ZEROS);
    },

    /**
     * Given a value in distance/sec, converts it to the equivilent distance in minutes/distance (pace).
     * Unit represents the number of meters or yards
     *
     * @param value the value to convert.
     * @param measurementSystem optional parameter that overrides the current state of the measurement system in the
     * personalizer.
     */
    personalizeSwimPace: function(value, measurementSystem) {
        var minutesPer100 = this.convertFractionalSwimPace(value, measurementSystem);
        return this.formatPace(minutesPer100);
    },

    /**
     * Given a value in distance/sec, converts it to the equivilent distance in minutes/distance, leaving minutes as a fraction
     * rather than breaking it down into seconds.
     *
     * @param value the value to convert.
     */
    convertFractionalPace: function(value) {
        if (value > 0) {
            var rawValue = 60 / this.convertSpeed(value);

            // For pace, the value approaches infinity as the user stops. We limit the speed
            // to 60 minute per unit.
            return Math.min(60, rawValue);
        } else {
            return 60;
        }
    },

    convertRowingPace: function(value) {
        if (!value) {
            return 0;
        }
        return 500 / (value * 60);
    },

    /** Converts the given speed value in meters (m/s) to the current measurement system, but without formatting
     * the number (adding commas, etc) or converting to pace.
     *
     * @param value the value to convert.
     */
    convertSpeed: function(value, measurementSystem, activityTypeKey) {
        if (!value) {
            return 0;
        }

        var speed = 0;

        measurementSystem = measurementSystem || this.measurementSystem;

        if (this.shouldUseMeasurementSystem(activityTypeKey)) {
            if (measurementSystem == this.MeasurementSystems.METRIC) {
                speed = value * this.ConversionFactors.MetersToKm * 3600; // km/hr
            } else {
                speed = value * this.ConversionFactors.MetersToMiles * 3600; // mi/hr
            }
        } else {
            switch (activityTypeKey) {
                case 'rowing':
                case ActivityTypeMapper.Rowing: //v2
                case 'indoor_rowing':
                    return this.convertRowingSpeed(value);
                case ActivityTypeMapper.OffShoreGrinding:
                case ActivityTypeMapper.OffShoreGrindingV2:
                case ActivityTypeMapper.OnShoreGrinding:
                case ActivityTypeMapper.OnShoreGrindingV2:
                case 'boating':
                case ActivityTypeMapper.Boating: //v2
                case 'sailing':
                case ActivityTypeMapper.Sailing: //v2
                case ActivityTypeMapper.Windsurfing:
                case ActivityTypeMapper.WindsurfingV2: //v2
                    return this.convertNauticalSpeed(value);
            }
        }

        return speed;
    },

    convertNauticalSpeed: function(value) {
        return value * ConversionFactors.MpsToKnots;
    },

    /**
     * Given a value in meters/sec, converts it to the floors/min.
     *
     * @param value to convert.
     *
     */

    convertFloorClimbingPace: function(value) {
        return (value * ConversionFactors.MpsToFeetPerMinute) / ConversionFactors.FloorsToFeet;
    },

    /**
     * Given a value in number of floors, converts it to the meters per second.
     *
     * @param value to convert.
     *
     */

    convertFloorsPerMinToMetersPerSecond: function(value) {
        return value * ConversionFactors.FloorsToFeet * ConversionFactors.FeetPerMinuteToMps;
    },

    convertRowingSpeed: function(value) {
        return value * ConversionFactors.MpsToKph;
    },

    /**
     * Given a speed in mph or kph, formats it using the current locale.
     */
    formatSpeed: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1_NOSEP);
    },

    poolUnitToMeasurementSystem: function(poolUnit) {
        if (poolUnit == 'yard') {
            return MeasurementSystems.STATUTE_US;
        } else if (poolUnit == 'meter') {
            return MeasurementSystems.METRIC;
        }

        return null;
    },

    measurementSystemToPoolUnit: function(measurementSystem) {
        if (measurementSystem === MeasurementSystems.METRIC) {
            return 'meter';
        } else if (measurementSystem === MeasurementSystems.STATUTE_US) {
            return 'yard';
        }

        return null;
    },

    /**
     * Personalizes speed values based on measurement system used, speed/pace preference, and locale.
     *
     * @param value the value to personalize.
     * @param activityType Optional parameter that can override the current speed type setting.
     * @param distanceOrPoolUnit distance is being phased out in favor of measurement system. We used to infer the pool length from
     * @param getZeroIfOverHour function will return a number type of 0 for pace if the pace is over 59:59, default to true
     * the distance of the activity.
     */
    personalizeSpeed: function(value, activityType, distanceOrPoolUnit, getZeroIfOverHour = true) {
        // It doesn't appear to handle 0 distance?  Speed returns zero.
        if (!value) {
            return 0;
        }

        var speedType = this.getWorkingSpeedType(activityType);

        switch (speedType) {
            case SpeedTypes.Speed:
            case SpeedTypes.Flying:
            case SpeedTypes.RowingSpeed:
            case SpeedTypes.NauticalSpeed:
            case SpeedTypes.ResortSkiSpeed:
            case SpeedTypes.WheelchairPace:
                var speed = this.convertSpeed(value, this.measurementSystem, activityType);
                return this.formatSpeed(speed);
                break;
            case SpeedTypes.SwimPace:
                var measurementSystem = this.measurementSystem;
                if (this.isLapSwimmingActivity(activityType)) {
                    // Meter and Yard pools are common in US. Infer the actual pace based on the distance swam, if available

                    if (distanceOrPoolUnit) {
                        measurementSystem =
                            this.poolUnitToMeasurementSystem(distanceOrPoolUnit) ||
                            this.inferPoolMeasurementSystemFromDistance(distanceOrPoolUnit);
                    }
                }
                return this.personalizeSwimPace(value, measurementSystem);

                break;
            case SpeedTypes.FloorClimbingPace:
                return this.formatSpeed(value);
                break;
            default:
                return this.personalizePace(value, null, activityType, getZeroIfOverHour);
                break;
        }
    },

    /**
     * Copy from GolfFormatter
     */
    zeroDash: function(input, percision) {
        if (input && input !== '0' && input !== 0) {
            return parseFloat(input).toFixed(percision);
        }

        return '--';
    },

    /**
     * Convert Club Speed (swing speed) to user preferred display unit
     *
     * @param input (m/s)
     * @param swingSpeedMeasureSystem
     * @returns {*}
     */
    personalizeGolfSwingSpeed: function(input, swingSpeedMeasureSystem, percision) {
        if (swingSpeedMeasureSystem) {
            if (swingSpeedMeasureSystem === 'metric_mps') {
                return this.zeroDash(input, percision);
            } else {
                return this.zeroDash(this.convertSpeed(input, swingSpeedMeasureSystem), percision);
            }
        } else {
            return this.zeroDash(this.convertSpeed(input), percision);
        }
    },

    personalizeSwimSpeed: function(value, measurementSystem) {
        var measurementSystem = measurementSystem || this.measurementSystem;

        var speed = this.convertSwimSpeed(value, measurementSystem);

        return this.formatSpeed(speed);
    },

    personalizeVelocity: function(value, measurementSystem) {
        var measurementSystem = measurementSystem || this.measurementSystem;

        var speed = this.convertVelocity(value, measurementSystem);

        return this.formatSpeed(speed);
    },

    /**
     * Given a distance in meters, attempts to infer whether the pool length is in yards or
     * meters.
     * @param distanceInMeters the length swam in meters.
     */
    inferPoolMeasurementSystemFromDistance: function(distanceInMeters) {
        if (!distanceInMeters) {
            return this.measurementSystem;
        }

        if (Math.round(distanceInMeters) == distanceInMeters) {
            return MeasurementSystems.METRIC; // Meters
        } else {
            return MeasurementSystems.STATUTE_US; // Yards
        }
    },

    /**
     * Given an activityType, returns the SpeedType used for formatting speed.
     * @param activityType the activity type we are getting the SpeedType for.
     */
    getSpeedTypeFromActivityType: function(activityType) {
        activityType = activityType || '';

        if (this.isLapSwimmingActivity(activityType)) {
            return SpeedTypes.SwimPace;
        } else if (this.isOpenWaterSwimmingActivity(activityType)) {
            return SpeedTypes.SwimPace;
        }

        if (ActivitiesUtil.isResortSkiActivity(activityType)) {
            return SpeedTypes.ResortSkiSpeed;
        }

        switch (activityType.toLowerCase()) {
            case 'flying':
                return SpeedTypes.Flying;
                break;
            case 'rowing':
            case ActivityTypeMapper.Rowing: //v2
            case 'indoor_rowing':
                return SpeedTypes.RowingPace;
                break;
            case 'running':
            case 'walking':
            case 'hiking':
            case ActivityTypeMapper.Rucking:
            case 'disc_golf':
            case 'trail_running':
            case 'street_running':
            case 'track_running':
            case 'treadmill_running':
            case 'speed_walking':
            case 'casual_walking':
            case 'stand_up_paddleboarding':
            case ActivityTypeMapper.StandUpPaddleboarding: //v2
            case 'virtual_run':
            case 'obstacle_run':
            case 'indoor_running':
            case 'ultra_run':
            case 'kayaking':
            case ActivityTypeMapper.Kayaking: // v2
            case 'paddling':
            case ActivityTypeMapper.Paddling: // v2
                return SpeedTypes.Pace;
                break;
            case 'floor_climbing':
                return SpeedTypes.FloorClimbingPace;
                break;
            case ActivityTypeMapper.OffShoreGrinding:
            case ActivityTypeMapper.OffShoreGrindingV2:
            case ActivityTypeMapper.OnShoreGrinding:
            case ActivityTypeMapper.OnShoreGrindingV2:
            case 'boating':
            case ActivityTypeMapper.Boating: //v2
            case 'sailing':
            case ActivityTypeMapper.Sailing: //v2
            case ActivityTypeMapper.Windsurfing:
            case ActivityTypeMapper.WindsurfingV2: //v2
                return SpeedTypes.NauticalSpeed;
                break;
            case 'wheelchair_push_run':
            case 'wheelchair_push_walk':
            case 'para_sports':
                return SpeedTypes.WheelchairPace;
            default:
                return SpeedTypes.Speed;
        }
    },

    getCadenceSensorTypeFromActivityType: function(activityType) {
        if (
            this.isLapSwimmingActivity(activityType) ||
            activityType === 'open_water_swimming' ||
            activityType === 'swimming'
        ) {
            return CadenceSensorTypes.Swim;
        }

        switch (activityType) {
            case 'running':
            case 'indoor_running':
            case 'obstacle_running':
            case 'street_running':
            case 'track_running':
            case 'trail_running':
            case 'treadmill_running':
            case 'virtual_run':
            case 'ultra_run':
            case 'obstacle_run':
            case 'dance':
                return CadenceSensorTypes.Ped;
                break;
            case 'skate_skiing_ws':
            case 'cross_country_skiing_ws':
                return CadenceSensorTypes.Ski;
                break;
            case ActivityTypeMapper.OffShoreGrinding:
            case ActivityTypeMapper.OffShoreGrindingV2:
            case ActivityTypeMapper.OnShoreGrinding:
            case ActivityTypeMapper.OnShoreGrindingV2:
                return CadenceSensorTypes.Grinding;
                break;
            case 'wheelchair_push_run':
            case 'wheelchair_push_walk':
            case 'para_sports':
                return CadenceSensorTypes.Wheelchair;
            case ActivityTypeMapper.Walking:
            case ActivityTypeMapper.SpeedWalking:
            case ActivityTypeMapper.CasualWalking:
            case ActivityTypeMapper.Hiking:
            case ActivityTypeMapper.DiscGolf:
                return CadenceSensorTypes.NonRunning;
            default:
                return CadenceSensorTypes.Bike;
        }
    },

    /**
     * Document Reference: https://confluence.garmin.com/pages/viewpage.action?pageId=653071524
     *
     * Personalizes duration from seconds to standard format.
     *
     * Format Guidelines
     *  - Duration in increments of hours                   = 2h
     *  - Duration in increments of hours and minutes       = 2h 15m
     *  - Duration in increments of minutes                 = 15m
     *  - Duration < 1 minute                               = 0m
     *
     * @param {number} seconds
     * @param t
     */
    personalizeStandardTimeDuration: function(seconds, t) {
        if (!seconds || seconds < 60) {
            return `0${t('units:duration_minute_short')}`;
        }
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        const formattedArray = [];

        if (hours) {
            formattedArray.push(`${hours}${t('units:duration_hour_short')}`);
        }
        if (!hours || minutes) {
            formattedArray.push(`${minutes}${t('units:duration_minute_short')}`);
        }

        return formattedArray.join(' ');
    },

    /**
     * Personalizes the duration from seconds to a readable format. Will always include
     * the hour even if it's 0.
     *
     * Example: 00:12:20
     */
    personalizeDurationForExport: function(value, showTenthsForShortDurations = true) {
        var duration = Personalizer.personalizeDuration(value, showTenthsForShortDurations),
            durationArray = duration.split(':');

        if (parseInt(durationArray[0], 10) < 10) {
            durationArray[0] = '0' + durationArray[0];
        }

        if (durationArray.length < 3) {
            durationArray.unshift('00');
        }

        return durationArray.join(':');
    },

    /**
     * Personalizes time values from seconds to a human readable form. Not personalized
     * based on locale though.
     *
     * Times less than 10 minutes should include tenths of seconds
     * Times greater than or equal to 9:59.95 should be rounded to the nearest whole second
     * @param {number} value the value to personalize.
     * @param {boolean} [showTenthsForShortDurations]
     * @param {boolean} [withoutSeconds] - send "true" as the third param if you need your duration formatted just in hours:minutes e.g: 9:59
     */
    personalizeDuration: function(value, showTenthsForShortDurations, withoutSeconds) {
        if (typeof showTenthsForShortDurations == 'undefined') {
            showTenthsForShortDurations = true;
        }

        var hours = Math.floor(value / 3600);
        var minutes = Math.floor(value / 60 - hours * 60);
        var rawSeconds = value - hours * 3600 - minutes * 60;
        var seconds = Math.floor(rawSeconds);
        var deltaSeconds = Math.round((rawSeconds - seconds) * 10000) / 10000.0;

        var hasFractionalPart = deltaSeconds > 0;
        var showTenthsOfSeconds = showTenthsForShortDurations && hasFractionalPart && value < 599.95; /* 9:59.95 */

        // handle rounding up
        if ((showTenthsOfSeconds && deltaSeconds >= 0.95) || (!showTenthsOfSeconds && deltaSeconds >= 0.5)) {
            seconds++;
            deltaSeconds = 0;
        }

        // handle rollover cases
        if (seconds > 59) {
            seconds = 0;
            minutes++;
            if (minutes > 59) {
                minutes = 0;
                hours++;
            }
        }

        var time =
            hours > 0
                ? hours + ':' + formatNumber(minutes, this.numberSeparator, NumberFormats.NO_DECIMAL_PAD_2_ZEROS)
                : formatNumber(minutes, this.numberSeparator, NumberFormats.NO_DECIMAL);

        if (withoutSeconds) {
            return time;
        }
        if (showTenthsOfSeconds) {
            seconds = seconds + deltaSeconds;
            time = time + ':' + formatNumber(seconds, this.numberSeparator, NumberFormats.PRECISION1_PAD_2_ZEROS);
        } else {
            time = time + ':' + formatNumber(seconds, this.numberSeparator, NumberFormats.NO_DECIMAL_PAD_2_ZEROS);
        }

        return time;
    },

    personalizeDanceDuration: function(value) {
        let hours = Math.floor(value / 3600);
        let minutes = Math.floor(value / 60 - hours * 60);
        let rawSeconds = value - hours * 3600 - minutes * 60;
        let seconds = Math.floor(rawSeconds);
        // handle rollover cases
        if (seconds > 59) {
            seconds = 0;
            minutes++;
            if (minutes > 59) {
                minutes = 0;
                hours++;
            }
        }

        let time =
            hours > 0
                ? hours + ':' + formatNumber(minutes, this.numberSeparator, NumberFormats.NO_DECIMAL_PAD_2_ZEROS)
                : formatNumber(minutes, this.numberSeparator, NumberFormats.NO_DECIMAL);

        time = time + ':' + formatNumber(seconds, this.numberSeparator, NumberFormats.NO_DECIMAL_PAD_2_ZEROS);

        return time;
    },

    /**
     * Personalizes values as Calories. Simply formats the value to 0 decimals.
     */
    personalizeCalories: function(value) {
        return formatNumber(value * ConversionFactors.JoulesToKiloCal, this.numberSeparator, NumberFormats.NO_DECIMAL);
    },

    personalizeCaloriesNotConvert: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.NO_DECIMAL);
    },

    convertCalories: function(value) {
        return value * ConversionFactors.JoulesToKiloCal;
    },

    convertCaloriesToJoules: function(value) {
        return value / ConversionFactors.JoulesToKiloCal;
    },

    /**
     * Personalizes the display of heart rate. Just rounds to nearest beat per minute.
     *
     * @param value the value to personalize.
     */
    personalizeHeartRate: function(value) {
        return this.formatWholeNumber(value);
    },

    /**
     * Personalizes the display of power. Just rounds to nearest watt.
     *
     * @param value the value to personalize.
     */
    personalizePower: function(value) {
        return this.formatWholeNumber(value);
    },

    personalizePowerKg: function(value) {
        return formatNumber(value, this.numberSepatator, NumberFormats.PRECISION2);
    },

    /**
     * Personalizes the display of left/right power balance. Rounds to nearest whole number. Note that this
     * only personalizes a left or a right value, it doesn't handle them together.
     *
     * @param value left or right balance value to personalize.
     */
    personalizeLeftRightBalance: function(value) {
        return this.formatWholeNumber(value);
    },

    /**
     * Personalizes the display of left/right torque effectiveness. Rounds to nearest whole number. Note that this
     * only personalizes a left or a right value, it doesn't handle them together.
     *
     * @param value left or right torque effectivness value to personalize.
     */
    personalizeTorqueEffectiveness: function(value) {
        return this.formatWholeNumber(value);
    },

    /**
     * Personalizes the display of left/right pedal smoothness. Rounds to nearest whole number. Note that this
     * only personalizes a left or a right value, it doesn't handle them together.
     *
     * @param value left or right pedal smoothness value to personalize.
     */
    personalizePedalSmoothness: function(value) {
        return this.formatWholeNumber(value);
    },

    /**
     * Personalizes the display of an intensity factor. Rounds to 3 decimal places
     *
     * @param value the value to personalize.
     */
    personalizeIntensityFactor: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION3);
    },

    /**
     * Personalizes the display Training Stress Score.  Rounds to nearest 10th.
     *
     * @param value the value to personalize.
     */
    personalizeTSS: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    /**
     * Personalizes work (joules) for power users.  Rounds to nearest whole.
     *
     * @param value the value to personalize.
     */
    personalizeWork: function(value) {
        return this.formatWholeNumber(value * ConversionFactors.CaloriesToJoules);
    },

    /**
     * Personalizes Cycling Dynamics standing count.
     * @param value
     */
    personalizeStandingCount: function(value) {
        return this.formatWholeNumber(value);
    },

    personalizeStandingTime: function(value) {
        if (!value) {
            return '--';
        }

        return this.personalizeDuration(value, false);
    },

    personalizePowerPhaseAngle: function(value) {
        if (value >= 360) {
            value -= 360;
        } else if (value < 0) {
            value += 360;
        }

        return this.formatWholeNumber(value);
    },

    personalizePowerPhaseArcLength: function(value) {
        return this.formatWholeNumber(value);
    },

    personalizePowerPhaseArcCenter: function(value) {
        return this.formatWholeNumber(value);
    },

    personalizePlatformCenterOffset: function(value) {
        if (value < -30 || value > 30) {
            return '';
        }

        return this.formatWholeNumber(value);
    },

    /**
     * Personalizes the display of cadence. Basically just rounds to whole unit of measure (ex. steps/cycles per minute).
     * As of CA-139895, also checks if the cadence value exists or is 0 to display "--" instead
     *
     * @param value the value to personalize.
     */
    personalizeCadence: function(value) {
        return !value ? '--' : this.formatWholeNumber(value);
    },

    convertCadence: function(value, activityType) {
        if (typeof value == 'undefined' || value == null) {
            return null;
        }

        var cadenceSensorType = this.getCadenceSensorTypeFromActivityType(activityType);

        if (cadenceSensorType == CadenceSensorTypes.Ped || cadenceSensorType == CadenceSensorTypes.NonRunning) {
            return value / 2;
        } else {
            return value;
        }
    },

    /**
     * Personalizes the display of strokes based on users number format preferences.
     * @param value
     */
    personalizeStrokes: function(value) {
        return this.formatWholeNumber(value);
    },

    /**
     * Personalizes the display of strokes based on users number format preferences.
     * @param value
     */
    personalizeSwolf: function(value) {
        return this.formatWholeNumber(value);
    },

    /**
     * Personalizes the display of stroke rate based on users number format preferences.
     * @param value
     */
    personalizeStrokeRate: function(value) {
        return value; //TODO Need to complete this when we see what the data looks like
    },

    /**
     * Personalizes the display of stroke distance based on users number format preferences.
     * @param value
     */
    personalizeStrokeDistance: function(value) {
        return value; //TODO Need to complete this when we see what the data looks like
    },

    personalizeVO2Max: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    /**
     * Personalizes the display of vertial oscilations. Should be 1 decimal.
     * @param value vertical oscillation value in cm
     * @returns {*}
     */
    personalizeVerticalOscillation: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    personalizeVerticalRatio: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    /**
     * Personalizes the display of step speed loss. Should be 1 decimal.
     * @param value in m/s
     * @returns {*} formatted value in cm/s
     */
    personalizeStepSpeedLoss: function(value) {
        // we receive from API in m/s , so we need to multiply by 100 to get the correct value in cm/s
        if (isNull(value) || isUndefined(value)) {
            return '--';
        }
        const transformedValue = value * 100;
        return formatNumber(transformedValue, this.numberSeparator, NumberFormats.PRECISION1);
    },

    /**
     * Personalizes the display of step speed loss percent. Should be 2 decimals.
     * @param value percentage
     * @returns {*} formatted value
     */
    personalizeStepSpeedLossPercentage: function(value) {
        if (isNull(value) || isUndefined(value)) {
            return '--';
        }
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION2);
    },

    /**
     * Formats the display of step speed loss. Should be 1 decimal.
     * @param value in cm/s
     * @returns {*} formatted value in cm/s
     */
    formatStepSpeedLoss: function(value) {
        if (isNull(value) || isUndefined(value)) {
            return '--';
        }

        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    /**
     * Personalizes the display of ground contact time. Formats as a whole number in ms.
     * @param value
     * @returns {*}
     */
    personalizeGroundContactTime: function(value) {
        return this.formatWholeNumber(value);
    },

    /**
     * Personalizes the display of training effect. Formats as a 1 decimal number.
     * @param value
     * @returns {*}
     */
    personalizeTrainingEffect: function(value) {
        return isNull(value) || isUndefined(value)
            ? '--'
            : formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    /**
     * Personalizes the display of stride length. Rounds to 2 decimal places.
     * @param value average stride length in cm
     * @returns {*}
     */
    personalizeAvgStrideLength: function(value) {
        return formatNumber(value * ConversionFactors.CmToMeters, this.numberSeparator, NumberFormats.PRECISION2);
    },

    convertStrideLength: function(value) {
        return value * ConversionFactors.CmToMeters;
    },

    convertDistancePerStroke: function(value) {
        if (this.measurementSystem !== this.MeasurementSystems.METRIC) {
            return value * ConversionFactors.MetersToFeet;
        }

        return value;
    },

    formatStrideLength: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION2);
    },

    formatDistancePerStroke: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION2);
    },

    /**
     * Given a temperature in C, converts it to the correct value based on the user's preference, without formatting it.
     * @param value
     */
    convertTemperature: function(value) {
        if (value != null) {
            if (this.measurementSystem == this.MeasurementSystems.STATUTE_US) {
                // convert Celsius to Fahrenheit
                return (value * 9) / 5 + 32;
            } else {
                return value;
            }
        }

        return value;
    },

    /**
     * Takes a converted value and converts it to a formatted value.
     * @param value
     */
    formatTemperature: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    /**
     * Personalizes the display of temperature as it comes from the Weather Service (in F).
     * @param value the value to personalize.
     */
    personalizeTemperature: function(value) {
        var convertedValue = this.convertTemperature(value);
        return this.formatTemperature(convertedValue);
    },

    /**
     * Personalizes the display of windspeed as it comes from the WeatherService. (knots).
     * @param value the value to personalize.
     */
    personalizeWindSpeed: function(value) {
        var convertedValue = null;
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            convertedValue = ConversionFactors.KnotsToKmph * value;
        } else {
            convertedValue = ConversionFactors.KnotsToMph * value;
        }

        return this.formatWholeNumber(convertedValue);
    },

    getWindSpeedUnitKey: function() {
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            return 'wind_speed_unit_metric';
        } else {
            return 'wind_speed_unit_statute';
        }
    },

    /**
     * Returns the supported measurement systems as an array.
     */
    supportedMeasurementSystems: function() {
        var supportedSystems = [];
        for (var measurementSystem in this.MeasurementSystems) {
            if (this.MeasurementSystems.hasOwnProperty(measurementSystem)) {
                // throws out inherited properties
                supportedSystems.push(measurementSystem);
            }
        }

        return supportedSystems;
    },

    /**
     * Returns the supported date formats as an array.
     */
    supportedDateFormats: function() {
        var supportedFormats = [];
        for (var dateFormat in this.DateFormats) {
            if (this.DateFormats.hasOwnProperty(dateFormat)) {
                // throws out inherited properties
                supportedSystems.push(dateFormat);
            }
        }

        return supportedFormats;
    },

    /**
     * Returns the key that will display the distance label based on the current
     * measurement system.
     *
     * @param activityType(optional): Distance unit may vary based on activity type
     * @param distance(optional): Required to infer unit types for swimming activities
     */
    getDistanceUnitKey: function(activityType, distance, poolUnit) {
        var isLapSwimmingActivity = this.isLapSwimmingActivity(activityType); // null or undefined is okay
        var isOpenWaterSwimming = this.isOpenWaterSwimmingActivity(activityType);

        if (isLapSwimmingActivity) {
            let measurementSystem;
            if (poolUnit) {
                measurementSystem = this.poolUnitToMeasurementSystem(poolUnit.unitKey || poolUnit.key);
            } else {
                measurementSystem = this.inferPoolMeasurementSystemFromDistance(distance);
            }

            if (measurementSystem == this.MeasurementSystems.METRIC) {
                return 'distance_unit_lap_swim_metric';
            } else {
                return 'distance_unit_lap_swim_statute';
            }
        } else if (isOpenWaterSwimming) {
            if (this.measurementSystem == this.MeasurementSystems.METRIC) {
                return 'distance_unit_lap_swim_metric';
            } else {
                return 'distance_unit_lap_swim_statute';
            }
        } else if (this.isNauticalActivity(activityType) && !this.isNauticalActivityException(activityType)) {
            // exception for Windsurfing Activity Types to show normal distance measurements as requested
            return 'distance_unit_nautical';
        } else if (this.isDisplayInMeter(activityType)) {
            return 'distance.meter';
        } else {
            if (this.measurementSystem == this.MeasurementSystems.METRIC) {
                return 'distance_unit_metric';
            } else {
                return 'distance_unit_statute';
            }
        }
    },

    /**
     * Should we display the duration in meters?
     * @param activityType
     */
    isDisplayInMeter: function(activityType) {
        return this.isRowingActivity(activityType) || ActivitiesUtil.isTrackRunningActivity(activityType);
    },

    /**
     * Returns the key that will display the swim distance label based on the current
     * measurement system.
     */
    getSwimDistanceUnitKey: function() {
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            return 'distance_unit_lap_swim_metric';
        } else {
            return 'distance_unit_lap_swim_statute';
        }
    },

    getRowingSpeedUnitKey: function() {
        return 'speed_unit_metric';
    },

    /**
     * Returns the key that will display for the BikeCadence Series Type, given a activity type key.
     * @param activityTypeKey
     */
    getBikeCadenceChartKey: function(activityTypeKey) {
        if (
            ActivitiesUtil.isOnShoreGrindingActivity(activityTypeKey) ||
            ActivitiesUtil.isOffShoreGrinding(activityTypeKey)
        ) {
            return 'label_cadence';
        } else {
            return 'label_bike_cadence';
        }
    },

    /**
     * Get the chart title key of the Run Cadence Chart given the activity type
     * @param {string} activityType
     */
    getRunCadenceChartTitleKey: function(activityType) {
        const cadenceSensorType = this.getCadenceSensorTypeFromActivityType(activityType);

        if (cadenceSensorType == CadenceSensorTypes.NonRunning) {
            return 'label_cadence';
        } else {
            return 'label_run_cadence';
        }
    },

    /**
     * Determine if a walking (or hiking or disc golf) cadence sensor type given the activity type
     * @param {string} activityType
     */
    isNonRunningCadenceSensorType: function(activityType) {
        const cadenceSensorType = this.getCadenceSensorTypeFromActivityType(activityType);

        if (cadenceSensorType == CadenceSensorTypes.NonRunning) {
            return true;
        }

        return false;
    },

    /**
     * Returns the key that will display the unit for pool distance, given a unit key.
     * @param unitKey
     */
    getPoolLengthUnitKey: function(unitKey) {
        if (unitKey == 'yard') {
            return 'pool_length_unit_statute';
        } else {
            return 'pool_length_unit_metric';
        }
    },

    /**
     * Returns the key that will display the elevation label based on the current
     * measurement system.
     */
    getElevationUnitKey: function() {
        return this.getMetersFtUnitKey();
    },

    /**
     * Returns the key that will display the surf wave distance label based on the current
     * measurement system.
     */
    getWaveDistanceUnitKey: function() {
        if (this.measurementSystem === this.MeasurementSystems.METRIC) {
            return 'elevation_unit_metric';
        } else {
            return 'elevation_unit_statute';
        }
    },

    /**
     * Returns the key that will display the power label based on the current
     * measurement system.
     */
    getPowerUnitKey: function() {
        return 'max_avg_power_unit';
    },

    /**
     * Returns the key that will display the power label based on zones.
     */
    getPowerZoneUnitKey: function() {
        return 'zone_unit';
    },

    /**
     * Returns the key that will display the power label based on the current
     * measurement system.
     */
    getPowerBalanceUnitKey: function() {
        return 'power_balance_unit';
    },

    getWorkUnitKey: function() {
        return 'work_unit';
    },

    /**
     * Returns the key that will display the date label based on the current measurement
     * system.
     */
    getDateFormatKey: function() {
        switch (this.dateFormat) {
            case this.DateFormats.MMDDYYYY:
                return 'MM/DD/YYYY';
            case this.DateFormats.DDMMYYYY:
                return 'DD/MM/YYYY';
            case this.DateFormats.YYYYMMDD:
                return 'YYYY/MM/DD';
            default:
                return 'MM/DD/YYYY';
        }
    },

    /**
     * Returns the key that will display the date label based on the current measurement
     * system but will truncate the format to exclude the year.
     */
    getShortDateFormatKey: function() {
        switch (this.dateFormat) {
            case this.DateFormats.MMDDYYYY:
                return 'MM/DD';
            case this.DateFormats.DDMMYYYY:
                return 'DD/MM';
            case this.DateFormats.YYYYMMDD:
                return 'MM/DD';
            default:
                return 'MM/DD';
        }
    },

    /**
     * Returns the key that will display the weight label based on the current measurement
     * system.
     */
    getDurationUnitKey: function() {
        return 'activity_timer_unit';
    },

    /**
     * Returns the key that will display the weight label based on the current measurement
     * system.
     * @param weightDisplayUnit(optional): user's preference of displaying weight if the measurement system is STATUTE_UK
     */
    getWeightUnitKey: function(weightDisplayUnit) {
        if (
            this.measurementSystem == this.MeasurementSystems.METRIC ||
            (this.measurementSystem === this.MeasurementSystems.STATUTE_UK &&
                weightDisplayUnit === Personalizer.WeightUnitKeys.METRIC)
        ) {
            return 'weight_unit_metric';
        } else {
            return 'weight_unit_statute';
        }
    },

    /**
     * Returns the key that will display the speed label based on the current measurement
     * system.
     */
    getSpeedUnitKey: function(activityTypeKey) {
        if (this.shouldUseMeasurementSystem(activityTypeKey)) {
            if (this.measurementSystem == this.MeasurementSystems.METRIC) {
                return 'speed_unit_metric';
            } else {
                return 'speed_unit_statute';
            }
        }

        if (activityTypeKey) {
            switch (activityTypeKey) {
                case 'rowing':
                case ActivityTypeMapper.Rowing: //v2
                case 'indoor_rowing':
                    return this.getRowingSpeedUnitKey();
                case ActivityTypeMapper.OffShoreGrinding:
                case ActivityTypeMapper.OffShoreGrindingV2:
                case ActivityTypeMapper.OnShoreGrinding:
                case ActivityTypeMapper.OnShoreGrindingV2:
                case 'boating':
                case ActivityTypeMapper.Boating: //v2
                case 'sailing':
                case ActivityTypeMapper.Sailing: //v2
                case ActivityTypeMapper.Windsurfing:
                case ActivityTypeMapper.WindsurfingV2: //v2
                    return this.getNauticalSpeedUnitKey();
            }
        }

        return 'speed_unit_statute';
    },

    getNauticalSpeedUnitKey: function() {
        return 'speed_unit_nautical';
    },

    getSwingSpeedUnitKey: function(swingSpeedMeasurementSystem) {
        if (swingSpeedMeasurementSystem) {
            switch (swingSpeedMeasurementSystem) {
                case 'metric_mps':
                    return 'speed_unit_metric_mps';
                    break;
                case this.MeasurementSystems.METRIC:
                    return 'speed_unit_metric';
                    break;
                default:
                    return 'speed_unit_statute';
                    break;
            }
        } else {
            return this.measurementSystem === this.MeasurementSystems.METRIC
                ? 'speed_unit_metric'
                : 'speed_unit_statute';
        }
    },

    golfSpeed: function(input, swingSpeedMeasureSystem) {
        if (swingSpeedMeasureSystem !== null) {
            if (swingSpeedMeasureSystem === 'metric_mps') {
                return Personalizer.convertSpeedToMetersPerSecond(input);
            } else if (swingSpeedMeasureSystem === Personalizer.measurementSystem) {
                return input;
            } else if (
                swingSpeedMeasureSystem === Personalizer.MeasurementSystems.METRIC &&
                Personalizer.measurementSystem !== Personalizer.MeasurementSystems.METRIC
            ) {
                return input * Personalizer.ConversionFactors.MilesToKm;
            } else if (
                swingSpeedMeasureSystem !== Personalizer.MeasurementSystems.METRIC &&
                Personalizer.measurementSystem === Personalizer.MeasurementSystems.METRIC
            ) {
                return input * Personalizer.ConversionFactors.KmToMiles;
            }
        }
        return input;
    },

    /**
     * Returns the key that will display the pace label based on the current measurement
     * system.
     */
    getPaceUnitKey: function(activityTypeKey) {
        if (this.shouldUseMeasurementSystem(activityTypeKey) || this.isNauticalActivityException(activityTypeKey)) {
            if (this.measurementSystem == this.MeasurementSystems.METRIC) {
                return 'pace_unit_metric';
            } else {
                return 'pace_unit_statute';
            }
        }

        if (activityTypeKey) {
            switch (activityTypeKey) {
                case 'indoor_rowing':
                case 'rowing':
                case ActivityTypeMapper.Rowing: //v2
                    return this.getRowingPaceUnitKey();
                case 'floor_climbing':
                    return this.getFloorClimbingPaceUnitKey();
                case ActivityTypeMapper.OffShoreGrinding:
                case ActivityTypeMapper.OffShoreGrindingV2:
                case ActivityTypeMapper.OnShoreGrinding:
                case ActivityTypeMapper.OnShoreGrindingV2:
                case 'boating':
                case ActivityTypeMapper.Boating: //v2
                case 'sailing':
                case ActivityTypeMapper.Sailing: //v2
                    return this.getNauticalPaceUnitKey();
            }
        }

        return 'pace_unit_statute';
    },

    /**
     * distanceOrMeasurementSystem: distance is being phased out in favor of measurement system. We used to infer the pool length from
     * the distance of the activity.
     */
    getSwimPaceUnitKey: function(distanceOrMeasurementSystem) {
        var measurementSystem = this.measurementSystem;
        if (
            distanceOrMeasurementSystem == this.MeasurementSystems.METRIC ||
            distanceOrMeasurementSystem == this.MeasurementSystems.STATUTE_UK ||
            distanceOrMeasurementSystem == this.MeasurementSystems.STATUTE_UK
        ) {
            measurementSystem = distanceOrMeasurementSystem;
        } else if (distanceOrMeasurementSystem) {
            measurementSystem = this.inferPoolMeasurementSystemFromDistance(distanceOrMeasurementSystem);
        }

        if (measurementSystem == this.MeasurementSystems.METRIC) {
            return 'swim_pace_unit_metric';
        } else {
            return 'swim_pace_unit_statute';
        }
    },

    /**
     * Returns the key that will display the pace label based on the current measurement
     * system.
     *
     * distanceOrMeasurementSystem: distance is being phased out in favor of measurement system. We used to infer the pool length from
     * the distance of the activity.
     */
    getSwimSpeedUnitKey: function(distanceOrMeasurementSystem) {
        var measurementSystem = this.measurementSystem;
        if (
            distanceOrMeasurementSystem == this.MeasurementSystems.METRIC ||
            distanceOrMeasurementSystem == this.MeasurementSystems.STATUTE_UK ||
            distanceOrMeasurementSystem == this.MeasurementSystems.STATUTE_UK
        ) {
            measurementSystem = distanceOrMeasurementSystem;
        } else if (distanceOrMeasurementSystem) {
            measurementSystem = this.inferPoolMeasurementSystemFromDistance(distanceOrMeasurementSystem);
        }

        if (measurementSystem == this.MeasurementSystems.METRIC) {
            return 'swim_speed_unit_metric_kph';
        } else {
            return 'swim_speed_unit_statute_mph';
        }
    },

    /**
     * Returns the key that will display the velocity label based on the current measurement system
     * system.
     */
    getVelocityUnitKey: function() {
        var measurementSystem = this.measurementSystem;

        if (measurementSystem == this.MeasurementSystems.METRIC) {
            return 'velocity_unit_metric';
        } else {
            return 'velocity_unit_statute';
        }
    },

    /**
     * Returns the key that will display the speed title based on the current SpeedType preference.
     * @param activityType: Optional parameter that can override the current SpeedType.
     * @param distanceOrMeasurementSystem (optional): The distance of the activity. Used to infer pool length unit types or can be meter or yard
     */
    getSpeedTypeKey: function(activityType, distanceOrMeasurementSystem, poolUnit) {
        var speedType = this.getWorkingSpeedType(activityType);

        switch (speedType) {
            case SpeedTypes.Pace:
                return this.getPaceUnitKey();
                break;
            case SpeedTypes.SwimPace:
                if (this.isLapSwimmingActivity(activityType)) {
                    if (poolUnit) {
                        distanceOrMeasurementSystem = this.poolUnitToMeasurementSystem(poolUnit.unitKey || poolUnit);
                    }
                    return this.getSwimPaceUnitKey(distanceOrMeasurementSystem);
                } else {
                    return this.getSwimPaceUnitKey(this.measurementSystem);
                }
                break;
            case SpeedTypes.SwimSpeed:
                return this.getSwimSpeedUnitKey(distanceOrMeasurementSystem);
                break;
            case SpeedTypes.RowingPace:
                return this.getRowingPaceUnitKey();
                break;
            case SpeedTypes.RowingSpeed:
                return this.getRowingSpeedUnitKey();
                break;
            case SpeedTypes.FloorClimbingPace:
                return this.getFloorClimbingPaceUnitKey();
                break;
            case SpeedTypes.NauticalSpeed:
                return this.getNauticalSpeedUnitKey();
                break;
            default:
                return this.getSpeedUnitKey();
        }
    },

    /**
     * Returns the key that will display the cadence label based on the current cadence sensor type.
     */
    getCadenceUnitKey: function(activityType) {
        if (activityType === 'wheelchair_push_run' || activityType === 'wheelchair_push_walk') {
            return 'wheelchair_cadence_unit';
        } else if (ActivitiesUtil.isJumpRopeActivity(activityType)) {
            return 'bike_cadence_unit';
        }
        var cadenceSensorType = this.getCadenceSensorTypeFromActivityType(activityType);

        if (cadenceSensorType == CadenceSensorTypes.Bike || cadenceSensorType == CadenceSensorTypes.Grinding) {
            return 'bike_cadence_unit';
        } else if (cadenceSensorType == CadenceSensorTypes.Ped || cadenceSensorType == CadenceSensorTypes.NonRunning) {
            return 'run_cadence_unit';
        } else if (cadenceSensorType == CadenceSensorTypes.Swim) {
            return 'cadence_unit_swim';
        } else if (cadenceSensorType == CadenceSensorTypes.Ski) {
            return 'ski_cadence_unit';
        } else if (cadenceSensorType == CadenceSensorTypes.Wheelchair) {
            return 'wheelchair_cadence_unit';
        }

        return '';
    },

    getStrokesUnitKey: function() {
        return 'strokes_unit';
    },

    getStrokeRateUnitKey: function() {
        return 'swim_cadence_unit';
    },

    getVerticalOscillationUnitKey: function() {
        return 'vert_oscillation_unit';
    },

    getVerticalRatioUnitKey: function() {
        return 'percentage_unit';
    },

    getStepSpeedLossUnitKey: function() {
        return 'avg_step_speed_loss_unit';
    },

    getStepSpeedLossPercentageUnitKey: function() {
        return 'percentage_unit';
    },

    getGroundContactTimeUnitKey: function() {
        return 'ground_contact_time_unit';
    },

    getAvgStrideLengthUnitKey: function() {
        return 'avg_stride_length_unit';
    },

    getRowingPaceUnitKey: function() {
        return 'pace_unit_metric_rowing';
    },

    getNauticalPaceUnitKey: function() {
        return 'pace_unit_nautical';
    },

    getFloorClimbingPaceUnitKey: function() {
        return 'floors_climbing_pace_unit';
    },

    getDistancePerStrokeUnitKey: function() {
        if (this.measurementSystem !== this.MeasurementSystems.METRIC) {
            return 'height_unit_ft';
        }

        return 'distance.meter';
    },

    /**
     * Returns the key to use to personalize calories.
     */
    getCalorieUnitKey: function() {
        return 'calories_unit';
    },

    /**
     * Returns the key to use when personalizing temperature values.
     */
    getTemperatureUnitKey: function() {
        if (this.measurementSystem == this.MeasurementSystems.STATUTE_US) {
            return 'temp_unit_statute';
        } else {
            return 'temp_unit_metric';
        }
    },

    getTotalWavesLabelKey: function() {
        return 'surf_waves_label';
    },

    getHeartRateUnitKey: function() {
        return 'heart_rate_unit';
    },

    getPercentMaxHeartRateUnitKey: function() {
        return 'heart_rate_percent_max_unit';
    },

    getHeartRateZoneUnitKey: function() {
        return 'zone_unit';
    },

    getPaceLabelKey: function() {
        return 'label_pace';
    },

    getPaceAbbrLabelKey: function() {
        return 'label_pace_abbr';
    },

    getSwimPaceLabelKey: function() {
        return 'label_swim_pace';
    },

    getSwimPaceAbbrLabelKey: function() {
        return 'label_swim_pace_abbr';
    },

    getSpeedLabelKey: function() {
        return 'label_speed';
    },

    getSpeedAbbrLabelKey: function() {
        return 'label_speed_abbr';
    },

    getAvgSpeedLabelKey: function() {
        return 'label_avg_speed';
    },

    getAvgMovingSpeedLabelKey: function() {
        return 'label_avg_moving_speed';
    },

    getMaxSpeedLabelKey: function() {
        return 'label_max_speed';
    },

    getAvgPaceLabelKey: function() {
        return 'label_avg_pace';
    },

    getAvgGradeAdjustedPaceLabelKey: function() {
        return 'avg_gap_abbr';
    },

    getAvgSwimPaceLabelKey: function(isSwimmingActivity) {
        if (isSwimmingActivity) {
            return 'label_avg_swim_pace';
        }
        return 'label_avg_pace';
    },

    getAvgMovingPaceLabelKey: function() {
        return 'label_avg_moving_pace';
    },

    getMaxPaceLabelKey: function() {
        return 'label_max_pace';
    },

    getMaxSwimPaceLabelKey: function(isSwimmingActivity) {
        if (isSwimmingActivity) {
            return 'label_max_swim_pace';
        }
        return 'label_max_pace';
    },

    getFlyingSpeedLabelKey: function() {
        return 'label_flying_speed';
    },

    getFlyingSpeedAbbrLabelKey: function() {
        return 'label_flying_speed_abbr';
    },

    getAvgFlyingSpeedLabelKey: function() {
        return 'label_avg_flying_speed';
    },

    getMaxFlyingSpeedLabelKey: function() {
        return 'label_max_flying_speed';
    },

    getMovingFlyingSpeedLabelKey: function() {
        return 'label_moving_flying_speed';
    },

    getAvgNauticalSpeedLabelKey: function(activityType) {
        if (ActivitiesUtil.isWindsurfingActivity(activityType)) {
            return 'label_avg_speed';
        }
        return 'avg_naut_speed_over_ground';
    },

    getMaxNauticalSpeedLabelKey: function(activityType) {
        if (ActivitiesUtil.isWindsurfingActivity(activityType)) {
            return 'label_max_speed';
        }
        return 'max_naut_speed_on_ground';
    },

    getLapCountLabelKey: function(activityType) {
        if (ActivitiesUtil.isResortSkiActivity(activityType)) {
            return 'number_of_runs';
        } else {
            return 'marq_number_laps';
        }
    },

    /**
     * Returns the key that will display the speed title based on the current SpeedType preference.
     * @param activityType: Optional parameter that can override the current SpeedType.
     * @param distance (optional): The distance of the activity. Used to infer pool length unit types.
     */
    getSpeedTypeLabelKey: function(activityType, distance, getAbbrevatedVersion) {
        var speedType = this.getWorkingSpeedType(activityType);

        switch (speedType) {
            case SpeedTypes.RowingSpeed:
            case SpeedTypes.Speed:
            case SpeedTypes.ResortSkiSpeed:
            case SpeedTypes.NauticalSpeed:
                if (getAbbrevatedVersion) {
                    return this.getSpeedAbbrLabelKey();
                }
                return this.getSpeedLabelKey();
                break;
            case SpeedTypes.Flying:
                if (getAbbrevatedVersion) {
                    return this.getFlyingSpeedAbbrLabelKey();
                }
                return this.getFlyingSpeedLabelKey();
                break;
            case SpeedTypes.SwimPace:
                if (getAbbrevatedVersion) {
                    return this.getSwimPaceAbbrLabelKey();
                }
                return this.getSwimPaceLabelKey();
                break;
            default:
                if (getAbbrevatedVersion) {
                    return this.getPaceAbbrLabelKey();
                }
                return this.getPaceLabelKey();
                break;
        }
    },

    /**
     * Returns the key that will display the average speed label based on the current SpeedType preference.
     * @param activityType: Optional parameter that can override the current SpeedType.
     * @param distance (optional): The distance of the activity. Used to infer pool length unit types.
     */
    getAvgSpeedTypeLabelKey: function(activityType, distance) {
        var speedType = this.getWorkingSpeedType(activityType);

        switch (speedType) {
            case SpeedTypes.Speed:
            case SpeedTypes.ResortSkiSpeed:
                return this.getAvgSpeedLabelKey();
                break;
            case SpeedTypes.Flying:
                return this.getAvgFlyingSpeedLabelKey();
                break;
            case SpeedTypes.NauticalSpeed:
                return this.getAvgNauticalSpeedLabelKey(activityType);
                break;
            default:
                return this.getAvgPaceLabelKey();
                break;
        }
    },

    /**
     * Returns the key that will display the average moving speed label based on the current SpeedType preference.
     * @param activityType: Optional parameter that can override the current SpeedType.
     * @param distance (optional): The distance of the activity. Used to infer pool length unit types.
     */
    getAvgMovingSpeedTypeLabelKey: function(activityType, distance) {
        var speedType = this.getWorkingSpeedType(activityType);

        switch (speedType) {
            case SpeedTypes.Speed:
            case SpeedTypes.ResortSkiSpeed:
                return this.getAvgMovingSpeedLabelKey();
                break;
            case SpeedTypes.Flying:
                return this.getMovingFlyingSpeedLabelKey();
                break;
            default:
                return this.getAvgMovingPaceLabelKey();
                break;
        }
    },

    /**
     * Returns the key that will display the max speed label based on the current SpeedType preference.
     * @param activityType: Optional parameter that can override the current SpeedType.
     * @param distance (optional): The distance of the activity. Used to infer pool length unit types.
     */
    getMaxSpeedTypeLabelKey: function(activityType, distance) {
        var speedType = this.getWorkingSpeedType(activityType);

        switch (speedType) {
            case SpeedTypes.Speed:
            case SpeedTypes.ResortSkiSpeed:
                return this.getMaxSpeedLabelKey();
                break;
            case SpeedTypes.Flying:
                return this.getMaxFlyingSpeedLabelKey();
                break;
            case SpeedTypes.NauticalSpeed:
                return this.getMaxNauticalSpeedLabelKey(activityType);
                break;
            default:
                return this.getMaxPaceLabelKey();
                break;
        }
    },

    /**
     * Returns the key that will display the cadence label based on activity type.
     * @param activityType: Optional parameter that can override the current SpeedType.
     */
    getAvgCadenceTypeLabelKey: function(activityType) {
        var cadenceSensorType = this.getCadenceSensorTypeFromActivityType(activityType);

        if (cadenceSensorType == CadenceSensorTypes.Bike) {
            return 'label_avg_bike_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Ped) {
            return 'label_avg_run_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Swim) {
            return 'label_avg_swim_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Ski) {
            return 'label_avg_ski_cadence';
        } else if (
            cadenceSensorType == CadenceSensorTypes.Grinding ||
            cadenceSensorType == CadenceSensorTypes.NonRunning
        ) {
            return 'label_avg_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Wheelchair) {
            return 'label_avg_wheelchair_cadence';
        }

        return '';
    },

    /**
     * Returns the key that will display the cadence label based on activity type.
     * @param activityType: Optional parameter that can override the current SpeedType.
     */
    getMaxCadenceTypeLabelKey: function(activityType) {
        var cadenceSensorType = this.getCadenceSensorTypeFromActivityType(activityType);

        if (cadenceSensorType == CadenceSensorTypes.Bike) {
            return 'label_max_bike_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Ped) {
            return 'label_max_run_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Swim) {
            return 'label_max_swim_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Ski) {
            return 'label_max_ski_cadence';
        } else if (
            cadenceSensorType == CadenceSensorTypes.Grinding ||
            cadenceSensorType == CadenceSensorTypes.NonRunning
        ) {
            return 'label_max_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Wheelchair) {
            return 'label_max_wheelchair_cadence';
        }

        return '';
    },

    /**
     * Returns the key that will display the cadence label based on activity type.
     * @param activityType: Optional parameter that can override the current SpeedType.
     */
    getCadenceTypeLabelKey: function(activityType) {
        var cadenceSensorType = this.getCadenceSensorTypeFromActivityType(activityType);

        if (cadenceSensorType == CadenceSensorTypes.Bike) {
            return 'label_bike_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Ped) {
            return 'label_run_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Ski) {
            return 'label_ski_cadence';
        } else if (
            cadenceSensorType == CadenceSensorTypes.Grinding ||
            cadenceSensorType == CadenceSensorTypes.NonRunning
        ) {
            return 'label_cadence';
        } else if (cadenceSensorType == CadenceSensorTypes.Wheelchair) {
            return 'label_wheelchair_cadence';
        }

        return '';
    },

    getElevationLabelKey: function(activityType) {
        var speedType = this.getWorkingSpeedType(activityType);

        return speedType === SpeedTypes.Flying ? 'label_altitude' : 'label_elevation';
    },

    getElevGainLabelKey: function(activityType) {
        const speedType = this.getWorkingSpeedType(activityType);

        if (speedType === SpeedTypes.Flying) {
            return 'label_altitude_gain';
        }

        if (
            ActivitiesUtil.isHikingActivity(activityType) ||
            ActivitiesUtil.isMountaineeringActivity(activityType) ||
            ActivitiesUtil.isRuckingActivity(activityType)
        ) {
            return 'label_ascent';
        }

        if (
            ActivitiesUtil.isIndoorClimbingActivity(activityType) ||
            ActivitiesUtil.isBoulderingActivity(activityType)
        ) {
            return 'label_climb_ascent';
        }

        return 'label_total_ascent';
    },

    getMaxSpeedLabelKey: function(activityType) {
        if (ActivitiesUtil.isOffShoreGrinding(activityType)) {
            return 'max_naut_speed_on_ground';
        }
        return 'label_max_speed';
    },

    getAvgSpeedLabelKey: function(activityType) {
        if (ActivitiesUtil.isOffShoreGrinding(activityType)) {
            return 'avg_naut_speed_over_ground';
        } else if (ActivitiesUtil.isSurfingActivity(activityType)) {
            return 'avg_surf_speed';
        }
        return 'label_avg_speed';
    },

    getDurationLabelKey: function(activityType) {
        if (
            ActivitiesUtil.isIndoorClimbingActivity(activityType) ||
            ActivitiesUtil.isBoulderingActivity(activityType) ||
            ActivitiesUtil.isOffShoreGrinding(activityType) ||
            ActivitiesUtil.isOnShoreGrindingActivity(activityType) ||
            ActivitiesUtil.isUltraRunActivity(activityType) ||
            ActivitiesUtil.isHiitActivity(activityType) ||
            ActivitiesUtil.isCardioBasedActivity(activityType) ||
            ActivitiesUtil.isSnorkelingActivity(activityType) ||
            ActivitiesUtil.isMobilityActivity(activityType)
        ) {
            return 'label_time_total';
        } else if (ActivitiesUtil.isSurfingActivity(activityType)) {
            return 'session_time_surf';
        }
        return 'label_time';
    },

    getStrokesLabelKey: function(activityType) {
        if (ActivitiesUtil.isCyclingActivity(activityType)) {
            return 'label_strokes_pedaling';
        }

        if (ActivitiesUtil.isLapSwimmingActivity(activityType)) {
            return 'label_strokes';
        }

        if (ActivitiesUtil.isParasportsActivity(activityType)) {
            return 'label_parasports_pushes';
        }

        return 'label_strokes_rowing';
    },

    getAverageDistancePerStrokeLabelKey: function(activityType) {
        if (ActivitiesUtil.isLapSwimmingActivity(activityType)) return 'label_avg_distance_per_stroke';

        return 'label_rowing_avg_distance_per_stroke';
    },

    getAverageSwimRateLabelKey: function(activityType) {
        if (ActivitiesUtil.isLapSwimmingActivity(activityType)) return 'label_avg_swim_stroke_rate';

        return 'label_stroke_rate';
    },

    getMaxSwimRateLabelKey: function(activityType) {
        if (ActivitiesUtil.isLapSwimmingActivity(activityType)) return 'label_max_swim_stroke_rate';

        return 'label_max_stroke_rate';
    },

    getTotalStrokesLabelKey: function(activityType) {
        if (ActivitiesUtil.isCyclingActivity(activityType)) {
            return 'label_total_pedaling_strokes';
        }

        if (ActivitiesUtil.isLapSwimmingActivity(activityType)) {
            return 'label_total_swim_strokes';
        }

        if (ActivitiesUtil.isParasportsActivity(activityType)) {
            return 'label_parasports_total_pushes';
        }

        return 'label_total_rowing_strokes';
    },

    getElevLossLabelKey: function(activityType) {
        const speedType = this.getWorkingSpeedType(activityType);

        if (speedType === SpeedTypes.Flying) {
            return 'label_altitude_loss';
        }

        return 'label_total_descent';
    },

    getElevMinLabelKey: function(activityType) {
        var speedType = this.getWorkingSpeedType(activityType);

        return speedType === SpeedTypes.Flying ? 'label_altitude_min' : 'label_min_elevation';
    },

    getElevMaxLabelKey: function(activityType) {
        var speedType = this.getWorkingSpeedType(activityType);

        return speedType === SpeedTypes.Flying ? 'label_altitude_max' : 'label_max_elevation';
    },

    getDynamicsLabelKey: function(activityType) {
        var cadenceSensorType = this.getCadenceSensorTypeFromActivityType(activityType);

        if (cadenceSensorType == CadenceSensorTypes.Ski) {
            return 'ski_dynamics_title';
        } else if (cadenceSensorType == CadenceSensorTypes.NonRunning) {
            return 'label_cadence';
        } else {
            return 'label_running_dynamics';
        }
    },

    getAvgPowerLabelKey: function(activityType) {
        var cadenceSensorType = this.getCadenceSensorTypeFromActivityType(activityType);

        if (cadenceSensorType == CadenceSensorTypes.Ski) {
            return 'label_avg_ski_power';
        } else {
            return 'label_avg_power';
        }
    },

    getMaxPowerLabelKey: function(activityType) {
        var cadenceSensorType = this.getCadenceSensorTypeFromActivityType(activityType);

        if (cadenceSensorType == CadenceSensorTypes.Ski) {
            return 'label_max_ski_power';
        } else {
            return 'label_max_power';
        }
    },

    /**
     * General purpose formatting functions.
     */

    /**
     * Given a time value in millisconds, formats it to a duration. Returns a value like "1:25:01"
     *
     * @param value the value to format.
     */
    fromMillisecondsToDuration: function(value) {
        var hoursRaw = value / 3600000;
        var hours = Math.floor(hoursRaw);

        var minutesRaw = (hoursRaw - hours) * 60;
        var minutes = Math.floor(minutesRaw);

        // round consistently with the activity details
        var seconds = Math.round((minutesRaw - minutes) * 60);

        if (seconds == 60) {
            seconds = 0;
            if (++minutes == 60) {
                minutes = 0;
                hours++;
            }
        }

        return this.formatDuration(hours, minutes, seconds);
    },

    /**
     * Given hours, number, and seconds, formats the string to display a duration.  Returns a
     * value like "1:25:01".
     *
     * @param hours the hours to use.
     * @param minutes the minutes to use.
     * @param seconds the seconds to use.
     */
    formatDuration: function(hours, minutes, seconds) {
        return (
            formatNumber(hours, NumberFormats.ONE_OR_TWO_DIGITS) +
            ':' +
            formatNumber(minutes, NumberFormats.TWO_DIGITS) +
            ':' +
            formatNumber(seconds, NumberFormats.TWO_DIGITS)
        );
    },

    /**
     * Description: Return a duration sting matching this format: https://confluence.garmin.com/pages/viewpage.action?pageId=653071524
     * Examples:
     *  10:52:17
     *  8:52:17
     *  52:17
     *  2:17
     *  0:17
     *
     * @param {Number} seconds
     *
     * @returns {String}
     */
    formatDurationFromSeconds: function(seconds) {
        // if seconds is not a number return an empty string
        if (!Number.isFinite(seconds)) {
            return '';
        }

        const hours = Math.floor(seconds / (60 * 60));
        const minutes = Math.floor((seconds % (60 * 60)) / 60);
        const remainingSeconds = Math.round(seconds % 60);

        const formattedHours = hours > 0 ? `${hours}:` : '';
        const formattedMinutes = hours > 0 && minutes < 10 ? `0${minutes}:` : `${minutes}:`;
        const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : `${remainingSeconds}`;

        return `${formattedHours}${formattedMinutes}${formattedSeconds}`;
    },

    /**
     * Given hours, number, and seconds, formats the string to display a pace.  Returns a
     * value like "08:01".
     *
     * @param hours the hours to use.
     * @param minutes the minutes to use.
     * @param seconds the seconds to use.
     */
    formatHoursMinsSecondsPace: function(hours, minutes, seconds) {
        var hoursString = '';
        if (hours > 0) {
            hoursString = formatNumber(hours, { maximumIntegerDigits: 2 }) + ':';
        }

        return (
            hoursString +
            formatNumber(minutes, NumberFormats.TWO_DIGITS) +
            ':' +
            formatNumber(seconds, NumberFormats.TWO_DIGITS)
        );
    },

    /**
     * Formats a number to a whole number using the locale so that it places separators into it.
     * @param value the value to format.
     */
    formatWholeNumber: function(value) {
        if (value == 0) {
            return 0;
        }

        return formatNumber(Math.round(value), this.numberSeparator);
    },

    /**
     * Formats a number to a decimal number using the locale so that it places separators into it.
     * @param value the value to format.
     * @param places number of decimal places
     */
    formatDecimalNumber: function(value, places, wholeNumberOption) {
        if (value == 0) {
            return 0;
        }

        var format = {
            useGrouping: true,
            minimumIntegerDigits: 1,
            minimumFractionDigits: places,
            maximumFractionDigits: places
        };

        //wholeNumberOption - ex: 44.0 would be 44
        if (wholeNumberOption) {
            var str = value.toString();
            if (str.indexOf('.') > -1) {
                var decimal = str.substring(str.indexOf('.'));
                if (decimal === '.0' || decimal === '.00') {
                    format.maximumFractionDigits = 0;
                    format.minimumFractionDigits = 0;
                }
            } else {
                format.maximumFractionDigits = 0;
                format.minimumFractionDigits = 0;
            }
        }

        return formatNumber(value, this.numberSeparator, format);
    },

    convertSpeedToMetersPerSecond: function(speed) {
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            return speed * this.ConversionFactors.KphToMps;
        } else {
            return speed * this.ConversionFactors.MphToMps;
        }
    },

    convertPaceToMetersPerSecond: function(pace) {
        var speed = this.calculateSpeed(1, pace / 3600);

        return this.convertSpeedToMetersPerSecond(speed);
    },

    convertSwimPaceToMetersPerSecond: function(pace, unit) {
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            return this.convertPaceToMetersPerSecond((pace * this.ConversionFactors.KmToMeters) / unit);
        } else {
            return this.convertPaceToMetersPerSecond((pace * this.ConversionFactors.MilesToYards) / unit);
        }
    },

    /**
     * Calculates speed as the quotient of distance/time.
     * If either are zero then zero is returned.
     * @param distance - The distance traveled
     * @param time - The time to travel the given distance
     * @return distance/time
     */
    calculateSpeed: function(distance, time) {
        if (distance == 0 || time == 0) {
            return 0;
        }
        return distance / time;
    },

    /**
     * Tests whether a value is a valid number.
     * @param value - The value to be tested.
     * @return true if the value is a number.
     */
    isNumeric: function(value) {
        if (isNaN(parseFloat(value))) {
            return false;
        }
        return true;
    },

    /**
     * Given a formatted time, returns time in seconds
     */
    parseTime: function(timeString) {
        var rval = 0;
        var value = 0;
        if (typeof timeString != 'undefined' && timeString != null && timeString.length > 0) {
            var parts = timeString.split(':');
            if (parts.length > 2) {
                if (this.isNumeric(parts[0])) {
                    value = parseFloat(parts[0]);
                    rval += value * 3600;
                }
                if (this.isNumeric(parts[1])) {
                    value = parseFloat(parts[1]);
                    rval += value * 60;
                }
                if (this.isNumeric(parts[2])) {
                    rval += parseFloat(parts[2]);
                }
            } else if (parts.length > 1) {
                if (this.isNumeric(parts[0])) {
                    value = parseFloat(parts[0]);
                    rval += value * 60;
                }
                if (this.isNumeric(parts[1])) {
                    rval += parseFloat(parts[1]);
                }
            } else {
                if (this.isNumeric(parts[0])) {
                    rval += parseFloat(parts[0]);
                }
            }
        }
        return rval;
    },

    /**
     * Personalizes weight without converting unit.
     *
     * @param value the value to personalize.
     */
    personalizeWeightWithoutConvert: function(value, optionalNumberFormat) {
        var numberFormat = optionalNumberFormat || NumberFormats.PRECISION1_NOZERO;
        return formatNumber(+value, this.numberSeparator, numberFormat);
    },

    /**
     * Personalizes body fat percentage
     *
     * @param value the value to personalize.
     */
    personalizeBodyFat: function(value, optionalNumberFormat) {
        var numberFormat = optionalNumberFormat || NumberFormats.PRECISION1_NOZERO;
        return formatNumber(value, this.numberSeparator, numberFormat);
    },

    /**
     * Personalizes weight based on measurement system used.
     *
     * @param value the value to personalize.
     * @param weightDisplayUnit(optional): user's preference of displaying weight if the measurement system is STATUTE_UK
     */
    personalizeWeight: function(value, optionalNumberFormat, weightDisplayUnit) {
        var numberFormat = optionalNumberFormat || NumberFormats.PRECISION1_NOZERO;
        return formatNumber(this.convertWeight(value, weightDisplayUnit), this.numberSeparator, numberFormat);
    },

    /**
     * Converts the given value in grams to the personalized value but without formatting it.
     * @param value the value to convert.
     * @param weightDisplayUnit(optional): user's preference of displaying weight if the measurement system is STATUTE_UK
     */
    convertWeight: function(value, weightDisplayUnit) {
        if (
            this.measurementSystem === this.MeasurementSystems.METRIC ||
            (this.measurementSystem === this.MeasurementSystems.STATUTE_UK &&
                weightDisplayUnit === Personalizer.WeightUnitKeys.METRIC)
        ) {
            value = value * this.ConversionFactors.GramsToKg;
        } else {
            value = value * this.ConversionFactors.GramsToLbs;
        }

        return +formatNumber(value, NumberFormats.PRECISION1_NOSEP_NOZERO);
    },

    /**
     * Converts the given value in grams to the value in given unit.
     * @param value the value to convert.
     * @toUnit the unit that should convert to
     */
    convertWeightToGivenUnit: function(value, toUnit, optionalNumberFormat) {
        var numberFormat = optionalNumberFormat || Personalizer.NumberFormats.PRECISION1_NOZERO;
        if (toUnit === Personalizer.WeightUnitKeys.STATUTE) {
            value = value * Personalizer.ConversionFactors.GramsToLbs;
        } else if (toUnit === Personalizer.WeightUnitKeys.STATUTE_UK) {
            value = value * Personalizer.ConversionFactors.GramsToSt;
        } else {
            value = value * Personalizer.ConversionFactors.GramsToKg;
        }
        return +formatNumber(value, this.numberSeparator, numberFormat).replace(/,/g, '.');
    },

    /**
     * Given the personalized (but not formatted) value, converts it to grams.
     * @param value the value to convert.
     */
    convertWeightToGrams: function(value) {
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            return value * this.ConversionFactors.KgToGrams;
        } else {
            return value * this.ConversionFactors.LbToGrams;
        }
    },

    convertWeightGramsToKg: function(value) {
        return value * this.ConversionFactors.GramsToKg;
    },

    /* Taken the weight by measurement system, converts it to grams to standardize - then converts to kg */
    convertWeightToKg: function(value) {
        if (
            this.measurementSystem === this.MeasurementSystems.STATUTE_US ||
            this.measurementSystem === this.MeasurementSystems.STATUTE_UK
        ) {
            return value * this.ConversionFactors.LbToGrams * this.ConversionFactors.GramsToKg;
        }

        return value; //metric should already be in kg
    },

    /**
     * Given the personalized (but not formatted) value in the given system, converts it to grams.
     * @param value the value to convert.
     * @param fromSystem the system of the value.
     */
    convertWeightToGramsBySystem: function(value, fromSystem) {
        if (fromSystem == Personalizer.WeightUnitKeys.METRIC) {
            return value * Personalizer.ConversionFactors.KgToGrams;
        } else if (fromSystem == Personalizer.WeightUnitKeys.STATUTE) {
            return value * Personalizer.ConversionFactors.LbToGrams;
        } else {
            return value * Personalizer.ConversionFactors.StToGrams;
        }
    },

    /**
     * Given the personalized (but not formatted) value in the given unit, converts it to grams.
     * @param value the value to convert.
     * @param fromUnit the unit of the value.
     */
    convertWeightToGramsByUnit: function(value, fromUnit) {
        if ('kilogram' === fromUnit) {
            return value * Personalizer.ConversionFactors.KgToGrams;
        } else if ('pound' === fromUnit) {
            return value * Personalizer.ConversionFactors.LbToGrams;
        } else {
            return value * Personalizer.ConversionFactors.StToGrams;
        }
    },

    /**
     * Given the personalized weight value in grams unit, converts it to stone and pounds.
     * @param weightGrams the grams value to convert
     */
    convertGramsToStLbs: function(weightGrams) {
        if (!weightGrams) {
            weightGrams = '0';
        }
        let weight = Math.abs((weightGrams * Personalizer.ConversionFactors.GramsToLbs).toFixed(1));
        return {
            stone: Math.floor(weight / this.ConversionFactors.StToLbs),
            pounds: Number((weight % this.ConversionFactors.StToLbs).toFixed(1))
        };
    },

    convertGramsOfCarbsToCalories: function(grams) {
        return grams * Personalizer.ConversionFactors.GramsOfCarbsToCalories;
    },

    /**
     * Given the personalized weight value in stone unit, converts it to stone and pounds.
     * @param weightSt the stone value to convert.
     */
    convertStoneToStLbs: function(weightSt) {
        var weightPounds;
        if (-(weightSt - parseInt(weightSt) - 1) < 0.001) {
            weightPounds = 0;
            weightSt = Math.round(weightSt);
        } else {
            weightPounds = (weightSt - parseInt(weightSt)) * Personalizer.ConversionFactors.StToLbs;
            weightPounds = Personalizer.personalizeWeightWithoutConvert(weightPounds);
            weightSt = parseInt(weightSt);
        }

        return { stone: weightSt, pounds: weightPounds };
    },

    /**
     * Given an activity type, determines if the activity is a swimming activity.
     * @param activityType
     */
    isSwimmingActivity: function(activityType) {
        return activityType === 'swimming' || activityType === 'lap_swimming' || activityType === 'open_water_swimming';
    },

    /**
     * Given an activity type, determines if the activity is a lap swimming activity.
     * @param activityType
     */
    isLapSwimmingActivity: function(activityType) {
        return activityType === 'lap_swimming';
    },

    isOpenWaterSwimmingActivity: function(activityType) {
        return activityType == 'swimming' || activityType == 'open_water_swimming';
    },

    isPaddleBoardingActivity: function(activityTypeKey) {
        return (
            activityTypeKey &&
            (activityTypeKey === 'stand_up_paddleboarding' ||
                activityTypeKey === ActivityTypeMapper.StandUpPaddleboarding) //v2
        );
    },

    isRowingActivity: function(activityTypeKey) {
        return (
            activityTypeKey &&
            (activityTypeKey === 'rowing' ||
            activityTypeKey === ActivityTypeMapper.Rowing || //v2
                activityTypeKey === 'indoor_rowing')
        );
    },

    isFloorClimbingActivity: function(activityTypeKey) {
        return activityTypeKey && activityTypeKey === 'floor_climbing';
    },

    isObstacleRunningActivity: function(activityTypeKey) {
        return activityTypeKey && activityTypeKey === 'obstacle_run';
    },

    // Originally from expanded chart view. Moved here so SeriesType could be extended for chart labels.
    isFlyingActivity: function(activityTypeKey) {
        var key = activityTypeKey;
        var speedType = this.getSpeedTypeFromActivityType(key);
        return speedType === this.SpeedTypes.Flying;
    },

    isNauticalActivity: function(activityTypeKey) {
        return ActivitiesUtil.isNauticalActivity(activityTypeKey);
    },

    isNauticalActivityException: function(activityTypeKey) {
        return ActivitiesUtil.isNauticalActivityException(activityTypeKey);
    },

    shouldUseMeasurementSystem: function(activityTypeKey) {
        return activityTypeKey &&
            (this.isRowingActivity(activityTypeKey) ||
                this.isFloorClimbingActivity(activityTypeKey) ||
                this.isNauticalActivity(activityTypeKey))
            ? false
            : true;
    },

    /**
     * Personalizes date based on date format used.
     *
     * @param value the value to personalize.
     */
    personalizeDate: function(value, format) {
        var formatDate = new Date(value);
        var yyyy = formatDate.getFullYear().toString();
        var mm = (formatDate.getMonth() + 1).toString(); // getMonth() is zero-based
        var dd = formatDate.getDate().toString();

        switch (format ? format : this.dateFormat) {
            case this.DateFormats.MMDDYYYY:
                return (mm.length > 1 ? mm : '0' + mm) + '/' + (dd.length > 1 ? dd : '0' + dd) + '/' + yyyy;
            case this.DateFormats.DDMMYYYY:
                return (dd.length > 1 ? dd : '0' + dd) + '/' + (mm.length > 1 ? mm : '0' + mm) + '/' + yyyy;
            case this.DateFormats.YYYYMMDD:
                return yyyy + '/' + (mm.length > 1 ? mm : '0' + mm) + '/' + (dd.length > 1 ? dd : '0' + dd);
            default:
                return (mm.length > 1 ? mm : '0' + mm) + '/' + (dd.length > 1 ? dd : '0' + dd) + '/' + yyyy;
        }
    },

    personalizeShortMonthDateRange: function(min, max, showAcrossYear, Localizer) {
        var formatMinDate;

        if (min instanceof Date) {
            formatMinDate = min;
        } else {
            formatMinDate = new Date(min);
        }

        var formatMaxDate;

        if (max instanceof Date) {
            formatMaxDate = max;
        } else {
            formatMaxDate = max;
        }

        var minYear = formatMinDate.getFullYear().toString();
        var minMonth = (formatMinDate.getMonth() + 1).toString();
        var minDay = formatMinDate.getDate().toString();

        minMonth = minMonth.length > 1 ? minMonth : '0' + minMonth;
        minMonth = Localizer.localize('month.abbr.' + minMonth);

        var maxYear = formatMaxDate.getFullYear().toString();
        var maxMonth = (formatMaxDate.getMonth() + 1).toString();
        var maxDay = formatMaxDate.getDate().toString();

        maxMonth = maxMonth.length > 1 ? maxMonth : '0' + maxMonth;
        maxMonth = Localizer.localize('month.abbr.' + maxMonth);

        switch (this.dateFormat) {
            case this.DateFormats.MMDDYYYY:
                if (minMonth == maxMonth && minYear == maxYear) {
                    return maxMonth + ' ' + minDay + ' - ' + maxDay + ', ' + maxYear;
                } else if (!showAcrossYear || minYear == maxYear) {
                    return minMonth + ' ' + minDay + ' - ' + maxMonth + ' ' + maxDay + ', ' + maxYear;
                } else {
                    return minMonth + ' ' + minDay + ', ' + minYear + ' - ' + maxMonth + ' ' + maxDay + ', ' + maxYear;
                }
            case this.DateFormats.DDMMYYYY:
                if (minMonth == maxMonth && minYear == maxYear) {
                    return minDay + ' - ' + maxDay + ' ' + maxMonth + ' ' + maxYear;
                } else if (!showAcrossYear || minYear == maxYear) {
                    return minDay + ' ' + minMonth + ' - ' + maxDay + ' ' + maxMonth + ' ' + maxYear;
                } else {
                    return minDay + ' ' + minMonth + ' ' + minYear + ' - ' + maxDay + ' ' + maxMonth + ' ' + maxYear;
                }
            case this.DateFormats.YYYYMMDD:
                if (minMonth == maxMonth && minYear == maxYear) {
                    return maxYear + ' ' + maxMonth + '  ' + minDay + ' - ' + maxDay;
                } else if (!showAcrossYear || minYear == maxYear) {
                    return maxYear + ' ' + minMonth + ' ' + minDay + ' - ' + maxMonth + ' ' + maxDay;
                } else {
                    return minYear + ' ' + minMonth + ' ' + minDay + ' - ' + maxYear + ' ' + maxMonth + ' ' + maxDay;
                }
            default:
                if (minMonth == maxMonth && minYear == maxYear) {
                    return maxMonth + ' ' + minDay + ' - ' + maxDay + ', ' + maxYear;
                } else if (!showAcrossYear || minYear == maxYear) {
                    return minMonth + ' ' + minDay + ' - ' + maxMonth + ' ' + maxDay + ', ' + maxYear;
                } else {
                    return minMonth + ' ' + minDay + ', ' + minYear + ' - ' + maxMonth + ' ' + maxDay + ', ' + maxYear;
                }
        }
    },

    /**
     * @deprecated - Use DateFormatter.formatFriendlyLongDateAndTime
     */
    personalizeDateAndTime: function(value) {
        return DateFormatter.formatFriendlyLongDateAndTime(value);
    },

    convertDateToMBDateString: function(dateObj) {
        var yyyy = dateObj.getFullYear();
        var mm = dateObj.getMonth() + 1; // getMonth() is zero-based
        var dd = dateObj.getDate();

        var h = dateObj.getHours();
        var m = dateObj.getMinutes();
        var s = dateObj.getSeconds();

        return (
            formatNumber(yyyy, NumberFormats.FOUR_DIGITS) +
            '-' +
            formatNumber(mm, NumberFormats.TWO_DIGITS) +
            '-' +
            formatNumber(dd, NumberFormats.TWO_DIGITS) +
            ' ' +
            formatNumber(h, NumberFormats.TWO_DIGITS) +
            ':' +
            formatNumber(m, NumberFormats.TWO_DIGITS) +
            ':' +
            formatNumber(s, NumberFormats.TWO_DIGITS)
        );
    },

    getWorkingSpeedType: function(activityType) {
        var speedType = this.speedType;
        if (activityType) {
            speedType = this.getSpeedTypeFromActivityType(activityType);
        }
        return speedType;
    },

    convertMetersToFloors: function(floorsInMeters) {
        return floorsInMeters / this.ConversionFactors.MetersToFloors;
    },

    personalizeMonth: function(value, i18next) {
        var formatDate;

        if (value instanceof Date) {
            formatDate = value;
        } else {
            formatDate = Moment(value)
                .utc()
                .toDate();
        }

        var mm = (formatDate.getMonth() + 1).toString(); // getMonth() is zero-based
        var mm2 = mm.length > 1 ? mm : '0' + mm;
        var month = i18next.t('common:month.abbr.' + mm2);
        return month;
    },

    getGroundContactBalance: function(left) {
        // Round the left side value to nearest 1/10 % (left side is the value in the FIT file).
        left = +left.toFixed(1);
        var right = 100 - left;
        return {
            left: this.formatDecimalNumber(left, 1),
            right: this.formatDecimalNumber(right, 1)
        };
    },

    getStrokeDistanceUnitKey: function() {
        return this.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC
            ? 'distance.meter'
            : 'elevation_unit_statute';
    },

    getAverageStrokeDistance: function(value) {
        var retVal;
        if (this.measurementSystem === this.MeasurementSystems.STATUTE_US) {
            retVal = value * this.ConversionFactors.MetersToFeet;
        } else {
            retVal = value;
        }
        return retVal;
    },

    /**
     * Given a value in meters/sec, converts it to the equivalent distance in minutes/500 meters.
     *
     * @param value the value to convert.
     * personalizer.
     */
    personalizeRowingPace: function(value) {
        var speed = this.convertRowingPace(value);
        return this.formatPace(speed);
    },

    /**
     * Given a value in meters/sec, converts it to the floors/min.
     *
     * @param value to convert.
     *
     */

    personalizeFloorClimbingPace: function(value) {
        if (!value) {
            return 0;
        }
        var speed = this.convertFloorClimbingPace(value);
        return this.formatFloorClimbingPace(speed);
    },

    formatFloorClimbingPace: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1_NOSEP);
    },

    displayDistanceAsWholeNumber: function(activityType) {
        if (activityType) {
            return (
                this.isSwimmingActivity(activityType) ||
                this.isRowingActivity(activityType) ||
                ActivitiesUtil.isTrackRunningActivity(activityType)
            );
        }
        return false;
    },

    /**
     *
     * Examples:
     *      formatValueByPrecision(1000, 2) ==> 1,000.00
     *      formatValueByPrecision(1234.1255, 2, false) ==> 1,234.12
     *      formatValueByPrecision(1234.1255, 2, true) ==> 1,234.13
     *      formatValueByPrecision(1234.1255, 2) ==> 1,234.13
     *      formatValueByPrecision(1234.1, 2) ==> 1,234.10
     *
     * @param value
     * @param precision
     * @param round - if not provided defaults to true
     * @returns {*}
     */
    formatValueByPrecision: function(value, precision, round) {
        if (isUndefined(value) || isNull(value) || isNaN(value)) {
            return value;
        }
        var hasPrecision = precision > 0,
            hasDecimal = ('' + value).indexOf('.') > -1,
            numberFormat = '##,##0' + (hasPrecision ? '.' : ''),
            index = 0;
        if (isUndefined(round) || isNull(round)) {
            round = true;
        }
        if (!hasDecimal && hasPrecision) {
            value = value.toFixed(precision);
        }
        for (; hasPrecision && index < precision; index++) {
            numberFormat += '0';
        }
        return formatNumber(+value, this.numberSeparator, numberFormat);
    },

    convertThousandsToKiloUnits: function(value, optionalNumberFormat) {
        // value -> value to convert to k's. Defaults to 0 if value is NaN.
        // optionalNumberFormat -> NumberFormats supported format.

        // Examples:
        // 1550 with no decimals => 1k, 1550 with 1 decimal => 1.5k, 1550 with 2 decimals => 1.55k

        var numberFormat = optionalNumberFormat || NumberFormats.NO_DECIMAL;

        value = Number(value) || 0;
        value = value / 1000;
        value = formatNumber(value, this.numberSeparator, numberFormat);

        return value + 'k';
    },

    convertDistanceToMeters: function(value) {
        if (isNaN(value)) {
            return 0;
        }

        var distanceUnitKey = DistanceUnits.mile;
        if (this.getMeasurementSystem() == MeasurementSystems.METRIC) {
            distanceUnitKey = DistanceUnits.kilometer;
        }

        return Conversion.convertToMeters(distanceUnitKey, value);
    },

    /**
     * @param birthDate YYYY-MM-DD format
     * @returns age in years
     */
    calculateAge: function(birthDate) {
        return Moment().diff(birthDate, 'years');
    },

    formatAvgGradient: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    /**
     * Given a value in number of floors, converts it to the meters / feet and formats as a whole number or with decimals.
     *
     * @param value the value to convert.
     *
     * @param decimals If true, formats the number with decimals
     */

    personalizeFloors: function(value, decimals) {
        if (!value) {
            return 0;
        }

        var convertedFloors = this.convertFloors(value);
        return this.formatElevation(convertedFloors, decimals);
    },

    /**
     * If the value is a whole number that number is returned else returns the number in a decimal format
     */
    personalizeGoalDurationPercentage: function(value) {
        if (value % 1 === 0) {
            return '' + value;
        }
        return Personalizer.formatDecimalNumber(value, 2);
    },

    /**
     * Given a value in number of floors, converts it to the meters or feet.
     *
     * @param value the value to convert.
     *
     */

    convertFloors: function(value) {
        if (this.measurementSystem === this.MeasurementSystems.STATUTE_US) {
            return value * this.ConversionFactors.FloorsToFeet;
        } else {
            return value * this.ConversionFactors.FloorsToMeters;
        }
    },

    personalizeDepth: function(value) {
        var depth = this.personalizeMetersFt(value);
        return depth || depth === 0 ? depth : '--';
    },

    getDepthUnitKey: function() {
        return this.getMetersFtUnitKey();
    },

    /**
     * Personalizes the given distance value in meters to the preferred measurement system
     * and format. For example, converts 334 (meters) to 1,096 (feet). Note that the label
     * is not returned.
     *
     * This is for use with distances.
     *
     * @param value: The value to be formatted. Should be an int representing meters.
     */
    personalizeDistanceMetersFt: function(value) {
        // Formatter doesn't appear to handle 0?  Returns blank. Handle here.
        if (!value) {
            return 0;
        }

        var convertedDistance = this.convertDistanceMetersFt(value);
        return this.formatMetersFt(convertedDistance);
    },

    /**
     * Given a distance in meters, converts it to the correct value for the current user's preference, but
     * without formatting it.
     *
     * This is for use with distance
     *
     * @param value the value to convert.
     */
    convertDistanceMetersFt: function(value) {
        var retVal = 0;

        if (this.measurementSystem === this.MeasurementSystems.METRIC) {
            retVal = value;
        } else {
            retVal = value * this.ConversionFactors.MetersToFeet;
        }

        return retVal;
    },

    /**
     * Personalizes the given distance value in meters to the preferred measurement system
     * and format. For example, converts 334 (meters) to 1,096 (feet). Note that the label
     * is not returned.
     *
     * This is for use with elevation which is handled differently than distance.
     *
     * @param value: The value to be formatted. Should be an int representing meters.
     * @param decimals Format with decimals
     */
    personalizeMetersFt: function(value, decimals) {
        // Formatter doesn't appear to handle 0?  Returns blank. Handle here.
        if (!value || isNaN(value)) {
            return 0;
        }

        var convertedDistance = this.convertMetersFt(value);
        return this.formatMetersFt(convertedDistance, decimals);
    },

    /**
     * Given a distance in meters, converts it to the correct value for the current user's preference, but
     * without formatting it.
     *
     * This is for use with elevation, which is handled differently than distance.
     *
     * @param value the value to convert.
     */
    convertMetersFt: function(value) {
        var retVal = 0;

        //Statute_UK uses metres for elevation
        if (this.measurementSystem === this.MeasurementSystems.STATUTE_US) {
            retVal = value * this.ConversionFactors.MetersToFeet;
        } else {
            retVal = value;
        }

        return retVal;
    },

    /**
     * This function takes a distance value from a converted value to its display value.
     * @param value
     * @param decimals Formats number with decimals
     */
    formatMetersFt: function(value, decimals) {
        if (decimals) {
            return this.formatDecimalNumber(value, 1);
        }
        return this.formatWholeNumber(value);
    },

    /**
     * Returns the key that will display the distance label based on the current
     * measurement system. (ft or meters)
     *
     * This is for elevation
     */
    getMetersFtUnitKey: function() {
        if (this.measurementSystem === this.MeasurementSystems.STATUTE_US) {
            return 'elevation_unit_statute';
        } else {
            return 'elevation_unit_metric';
        }
    },

    convertKgPerLiterToGramPerLiter: function(value) {
        return value * this.ConversionFactors.KgPerLiterToGramPerLiter;
    },

    personalizeDiveWaterType: function(waterType, waterDensity, i18next) {
        switch (waterType) {
            case 0: // FRESH
            case 'FRESH':
                return i18next.t('diving:water_type_fresh');
                break;
            case 1: // SALT
            case 'SALT':
                return i18next.t('diving:water_type_salt');
                break;
            case 2: // EN13319 - not used currently... but still in the spec - show as custom
            case 3: // CUSTOM
            case 'CUSTOM':
                // second argument in the localize is for the case when the namespace is defined at a higher level (e.g. in DivingActivityListItem component)
                return this.personalizeWaterDensity(waterDensity) + ' ' + i18next.t('units:water_density_unit');
                break;
            case 255: // INVALID
            case 'UNKNOWN':
            default:
                return '--';
                break;
        }
    },

    personalizeWaterDensity: function(waterDensity) {
        return waterDensity
            ? formatNumber(waterDensity / 1000, this.numberSeparator, NumberFormats.PRECISION3_NOSEP)
            : '--';
    },

    personalizeDiveGas: function(heliumContent, oxygenContent, i18next) {
        if (oxygenContent === 100) {
            return i18next.t('diving:gas_type_oxygen');
        } else if (oxygenContent === 21 && heliumContent === 0) {
            return i18next.t('diving:gas_type_air');
        } else if (oxygenContent !== 21 && oxygenContent !== 0 && heliumContent === 0) {
            return i18next.t('diving:gas_type_eanx');
        } else if (heliumContent > 0) {
            return i18next.t('diving:gas_type_trimix');
        } else {
            return '--';
        }
    },

    personalizeDiveGasMode: function(mode, i18next) {
        if (mode === 0) {
            // 0 - OPEN_CIRCUIT
            return i18next.t('diving:diving_open_circuit');
        } else if (mode === 1) {
            // 1 - CLOSED_CIRCUIT
            return i18next.t('diving:diving_closed_circuit');
        } else {
            // 255 - INVALID
            return '--';
        }
    },

    personalizeTankStatus: function(gasIndex, gasStatus, diveType, i18next) {
        if (gasIndex === 0 && diveType === 'ccr_diving') {
            return i18next.t('diving:diluent');
        } else if (gasIndex === 0) {
            return i18next.t('diving:bottom_gas');
        } else if (gasStatus === 1) {
            return i18next.t('diving:deco_enabled');
        } else if (gasStatus === 2) {
            return i18next.t('diving:backup_only');
        } else {
            return '--';
        }
    },

    personalizeDiveWeight: function(weight) {
        if (!weight) {
            return '--';
        }
        return formatNumber(weight, this.numberSeparator, NumberFormats.PRECISION1);
    },

    personalizeSuitThickness: function(thickness) {
        if (!thickness) {
            return '--';
        }
        return formatNumber(thickness, this.numberSeparator, NumberFormats.NO_DECIMAL_NOSEP);
    },

    personalizeWaterCurrentType: function(currentType) {
        if (!currentType) {
            return '--';
        }
        return currentType;
    },

    /** Diving API provides values slightly different than GC API */
    personalizeWaterCurrentTypeDivingAPI: function(waterCurrentType, i18next) {
        switch (waterCurrentType) {
            case 'NONE':
            case 'None':
                return i18next.t('diving:water_current_none');
                break;
            case 'LIGHT':
            case 'Light':
                return i18next.t('diving:water_current_light');
                break;
            case 'STRONG':
            case 'Strong':
                return i18next.t('diving:water_current_strong');
                break;
            case 'MODERATE':
            case 'Moderate':
                return i18next.t('diving:water_current_moderate');
                break;
            case 'UNKNOWN':
            case 'Unknown':
                return '--';
                break;
            default:
                return '--';
                break;
        }
    },

    personalizeWaterSurfaceCondition: function(surfaceCondition) {
        if (!surfaceCondition) {
            return '--';
        }
        return surfaceCondition;
    },

    personalizePressure: function(pressure) {
        if (!pressure) {
            return '--';
        }
        return formatNumber(pressure, this.numberSeparator, NumberFormats.NO_DECIMAL);
    },

    personalizePressureUnit: function(pressureUnit, i18next) {
        const pressureUnitKey = this.personalizePressureUnitKey(pressureUnit);
        if (pressureUnitKey) {
            return i18next.t(`units:${pressureUnitKey}`);
        }
        return ''; // pressure unit should be defined if there is a pressure. This is the case for '--' so return empty
    },

    personalizePressureUnitKey: function(pressureUnit) {
        const unitKey = pressureUnit && pressureUnit.unitKey ? pressureUnit.unitKey.toLowerCase() : '';
        if (unitKey === 'bar') {
            return 'pressure_unit_metric';
        } else if (unitKey === 'psi') {
            return 'pressure_unit_statute';
        } else {
            return ''; // pressure unit should be defined if there is a pressure. This is the case for '--' so return empty
        }
    },

    personalizeSacRate: function(sacRate) {
        if (!sacRate) {
            return '--';
        }
        return formatNumber(sacRate, this.numberSeparator, NumberFormats.PRECISION3_NOSEP_NOZERO);
    },

    personalizeSacRateUnit: function(sacRate, sacRateUnit, i18next) {
        if (typeof sacRate === 'undefined' || sacRate === null) {
            return '';
        }
        if (sacRateUnit && sacRateUnit.unitKey && sacRateUnit.unitKey === 'bar') {
            return i18next.t('units:sac_rate_unit_metric');
        } else if (sacRateUnit && sacRateUnit.unitKey) {
            return i18next.t('units:sac_rate_unit_statute');
        } else {
            return '';
        }
    },

    personalizeVolume: function(volume) {
        if (!volume) {
            return '--';
        }
        return formatNumber(volume, this.numberSeparator, NumberFormats.PRECISION1_NOZERO);
    },

    personalizeVolumeUnit: function(volumeUnit, i18next) {
        if (volumeUnit && volumeUnit.unitKey && volumeUnit.unitKey === 'liter') {
            return i18next.t('units:volume_unit_metric');
        } else if (volumeUnit && volumeUnit.unitKey) {
            return i18next.t('units:volume_unit_statute');
        } else {
            return ''; // volume unit should be defined if there is a volume. This is the case for '--' so return empty
        }
    },

    personalizeDiveWeightUnit: function(weightUnit, i18next) {
        if (weightUnit && weightUnit.unitKey === 'kilogram') {
            return i18next.t('units:weight_unit_metric');
        } else if (weightUnit) {
            return i18next.t('units:weight_unit_statute');
        } else {
            return ''; // weight unit should be defined if there is a weight. This is the case for '--' so return empty
        }
    },

    personalizerNumberOfDives: function(numberOfDives) {
        if (!numberOfDives) {
            return '--';
        }
        return formatNumber(numberOfDives, this.numberSeparator, NumberFormats.NO_DECIMAL_NOSEP);
    },

    personalizeNumberOfLaps: function(numberOfLaps) {
        if (!numberOfLaps) {
            return '--';
        }
        return formatNumber(numberOfLaps, this.numberSeparator, NumberFormats.NO_DECIMAL_NOSEP);
    },

    personalizeDiveConservatism: function(gfLow, gfHigh, i18next) {
        if (isUndefined(gfLow) || isNull(gfLow) || isUndefined(gfHigh) || isNull(gfHigh)) {
            return null;
        }

        if (gfLow === 35 && gfHigh === 75) {
            return i18next.t('diving:dive_conservatism_high');
        } else if (gfLow === 40 && gfHigh === 85) {
            return i18next.t('diving:dive_conservatism_medium');
        } else if (gfLow === 45 && gfHigh === 95) {
            return i18next.t('diving:dive_conservatism_low');
        } else {
            return i18next.t('diving:dive_conservatism_custom');
        }
    },

    personalizeVisibility: function(visibility) {
        if (!visibility) {
            return '--';
        }
        return formatNumber(visibility, this.numberSeparator, NumberFormats.NO_DECIMAL_NOSEP);
    },

    personalizeVisibilityUnit: function(visibilityUnit, i18next) {
        if (visibilityUnit && visibilityUnit.unitKey === 'meter') {
            return i18next.t('units:distance.meter');
        } else if (visibilityUnit) {
            return i18next.t('units:height_unit_ft');
        } else {
            return '';
        }
    },

    /** Diving API provides values slightly different than GC API */
    personalizeVisibilityUnitDivingAPI: function(visibilityUnit, i18next) {
        if (visibilityUnit && (visibilityUnit === 'METER' || visibilityUnit === 'meter')) {
            return i18next.t('units:distance.meter');
        } else if (visibilityUnit && (visibilityUnit === 'FOOT' || visibilityUnit === 'foot')) {
            return i18next.t('units:height_unit_ft');
        } else {
            return '';
        }
    },

    formatVectorAirCdA: function(value) {
        if (!value) {
            return '--';
        }

        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION3);
    },

    convertCmPerMSToMps: function(value) {
        //convert centimeter per milliseconds to meter per second
        return value * this.ConversionFactors.CmPerMsToMps;
    },

    convertCmToM: function(value) {
        //convert centimeter to meter
        return value * this.ConversionFactors.CmToMeters;
    },

    convertMsToSeconds: function(value) {
        //convert millisecond to seconds
        return value / this.ConversionFactors.SecondsToMs;
    },

    convertSecondsToMs: function(value) {
        //convert seconds to milliseconds
        return value * this.ConversionFactors.SecondsToMs;
    },

    personalizeSeconds: function(seconds, i18next) {
        if (seconds < 60) {
            // seconds
            return seconds + ' ' + i18next.t('activity-api:sec.label');
        } else if (seconds >= 60 && seconds < 3600) {
            // minutes
            return Math.round(seconds / 60) + ' ' + i18next.t('units:activity_duration_unit');
        } else {
            // hours
            return Math.round(seconds / 3600) + ' ' + i18next.t('units:duration_hour');
        }
    },

    convertToWattsPerKg: function(val, weight) {
        if (weight && weight > 0) {
            var weightInKg = Personalizer.convertWeightGramsToKg(weight);
            return Personalizer.personalizePowerKg(val / weightInKg);
        } else {
            return val;
        }
    },

    personalizeHangtime: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1_NOSEP);
    },

    personalizeMTBRotations: function(value) {
        if (value === null) {
            return '--';
        } else {
            return parseInt(value);
        }
    },

    personalizeMTBHeight: function(value, Localizer) {
        if (value === null) {
            return '--';
        }
        // Use convertDistanceMetersFt for jump distance because Statute_UK uses ft for jump elevation
        var convertedDistance = this.convertDistanceMetersFt(parseFloat(value)),
            formatted = formatNumber(convertedDistance, this.numberSeparator, NumberFormats.PRECISION2);
        return formatted + ' ' + Localizer.localize(this.personalizeMTBHeightUnitKey());
    },

    personalizeMTBHeightUnitKey: function() {
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            return 'distance.meter';
        } else {
            return 'height_unit_ft';
        }
    },

    personalizeMTBSeconds: function(seconds, Localizer) {
        if (seconds === null) {
            return '--';
        }
        var formatted = formatNumber(parseFloat(seconds), this.numberSeparator, NumberFormats.PRECISION2);
        return formatted + ' ' + Localizer.localize('duration_second_short');
    },

    peronalizeMTBFlow: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    peronalizeMTBGrit: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    personalizeFluid: function(value, fluidUnit, isGoal) {
        if (value === 0) {
            return 0;
        } else if (!value) {
            return '--';
        }

        var unit = fluidUnit || null;

        if (!unit) {
            if (this.measurementSystem === MeasurementSystems.STATUTE_US) {
                unit = FluidTypes.cup;
            } else {
                unit = FluidTypes.ml;
            }
        }

        var convertedValue = this.convertFluid(value, unit, isGoal);
        return this.formatFluidValue(convertedValue, unit);
    },

    convertFluid: function(value, unit, isGoal) {
        if (value === null || value === undefined || unit === null || unit === undefined) {
            return null;
        }

        if (typeof isGoal === 'undefined' || isGoal === null) {
            isGoal = false;
        }
        let newValue;

        switch (unit) {
            case FluidTypes.ml:
                if (isGoal) {
                    return Math.ceil(value);
                } else {
                    return Math.floor(value);
                }
            case FluidTypes.oz:
                newValue = value / ConversionFactors.FluidOuncesToMilliliters;
                if (isGoal) {
                    // the division is not always giving fixed results, so we will round up the values based on the first two decimals only
                    // example for 39 cups: 9226.932 / 236.588 = * 39.00000000000001
                    return Math.ceil(Math.floor(100 * newValue) / 100);
                } else {
                    return Math.floor(newValue);
                }
            case FluidTypes.cup:
                newValue = value / ConversionFactors.CupsToMilliliters;
                if (isGoal) {
                    return Math.ceil(Math.floor(100 * newValue) / 100);
                } else {
                    return Math.floor(newValue);
                }
        }
    },

    formatFluidValue: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.NO_DECIMAL);
    },

    getFluidUnitKey: function(unit, value) {
        switch (unit) {
            case FluidTypes.ml:
                return 'ml_unit_abbr';
            case FluidTypes.oz:
                return 'oz_unit_abbr';
            case FluidTypes.cup:
                return value && +value === 1 ? 'cup_unit_sg' : 'cup_unit_pl'; // We won't be using abbreviated names for "cups" and we won't be translating either
        }
    },

    personalizeLatLon: function(lat, lon) {
        if (lat && lon) {
            lat = parseFloat(lat);
            lon = parseFloat(lon);
            return lat.toFixed(6) + ', ' + lon.toFixed(6);
        } else {
            return '';
        }
    },

    getAltitudeAcclimationHeight: function(heightType) {
        if (this.measurementSystem === this.MeasurementSystems.METRIC) {
            if (heightType === TrainingStatusUtil.HeightTypes.BASIC) {
                return TrainingStatusUtil.TrainingMetrics.basicTrainingHeightInMeters;
            } else {
                return TrainingStatusUtil.TrainingMetrics.maxAltitudeAcclimationHeightMeters;
            }
        } else {
            if (heightType === TrainingStatusUtil.HeightTypes.BASIC) {
                return TrainingStatusUtil.TrainingMetrics.basicTrainingHeightInFeet;
            } else {
                return TrainingStatusUtil.TrainingMetrics.maxAltitudeAcclimationHeightFeet;
            }
        }
    },

    personalizeAltitudeAcclimation: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.NO_DECIMAL);
    },

    getAltitudeAcclimationHeightUnit: function(expand) {
        if (this.measurementSystem === this.MeasurementSystems.METRIC) {
            return expand ? 'label_meters' : 'distance.meter';
        } else {
            return expand ? 'height_unit_feet' : 'height_unit_ft';
        }
    },

    getHeatAcclimationValue: function() {
        if (this.measurementSystem === this.MeasurementSystems.STATUTE_US) {
            return TrainingStatusUtil.TrainingMetrics.temperatureFahrenheit;
        } else {
            return TrainingStatusUtil.TrainingMetrics.temperatureCelsius;
        }
    },

    getHeatAcclimationUnitEmptyState: function() {
        if (this.measurementSystem === this.MeasurementSystems.STATUTE_US) {
            return String.fromCharCode(176) + 'F';
        } else {
            return String.fromCharCode(176) + 'C';
        }
    },

    getRespirationUnitKey: function() {
        return 'label_respiration_rate_unit';
    },

    personalizeExerciseLoad: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.NO_DECIMAL);
    },

    /**
     * Personalize respiration rate - whole number - no rounding
     * @param value
     * @returns {*}
     */
    personalizeRespiration: function(value) {
        if (isNull(value) || isUndefined(value)) {
            return '--';
        }

        return formatNumber(value, this.numberSeparator, NumberFormats.NO_DECIMAL);
    },

    /**
     * Personalize stress - whole number - round down
     * @returns {*}
     */
    personalizeStress: function(value) {
        if (isNull(value) || isUndefined(value)) {
            return '--';
        }

        return formatNumber(value, this.numberSeparator, NumberFormats.NO_DECIMAL);
    },

    /**
     * Personalize active sets - whole number - round down
     * @returns {*}
     */
    personalizeActiveSets: function(value) {
        if (isNull(value) || isUndefined(value)) {
            return '--';
        }

        return formatNumber(value, this.numberSeparator, NumberFormats.NO_DECIMAL);
    },

    /**
     * Personalize floors climbed - whole number - round down - replace 0 with dashes
     * @returns {*}
     */
    personalizeFloorsClimbed: function(value) {
        if (!value) {
            return '--';
        }

        return formatNumber(value, this.numberSeparator, NumberFormats.NO_DECIMAL);
    },

    /**
     * Given a time value in seconds, formats it to a duration.
     * Returns a value like "01:25.01" - minutes:seconds.milliseconds
     * Returns a value over an hour "01:02:50.01" - hours:minutes:seconds.milliseconds
     */
    fromSecondsToDuration: function(value) {
        var hoursRaw = value / 3600;
        var hours = Math.floor(hoursRaw);

        var minutesRaw = (hoursRaw - hours) * 60;
        var minutes = Math.floor(minutesRaw);

        var secondsRaw = (minutesRaw - minutes) * 60;
        var seconds = Math.floor(secondsRaw);

        var milliseconds = Math.floor((secondsRaw - seconds) * 1000) / 10;

        if (seconds === 60) {
            seconds = 0;
            if (++minutes === 60) {
                minutes = 0;
                hours++;
            }
        }

        if (value >= 3600) {
            return this.formatDurationHrsMinSecMil(hours, minutes, seconds, milliseconds);
        }
        return this.formatDurationMinSecMil(minutes, seconds, milliseconds);
    },

    /**
     * Given minutes, seconds, and milliseconds, formats the string to display a duration.
     * Returns a value like "01:25.01". - minutes:seconds.milliseconds
     *
     * @param minutes the minutes to use.
     * @param seconds the seconds to use.
     * @param milliseconds the milliseconds to use.
     */
    formatDurationMinSecMil: function(minutes, seconds, millisecond) {
        return (
            formatNumber(minutes, NumberFormats.TWO_DIGITS) +
            ':' +
            formatNumber(seconds, NumberFormats.TWO_DIGITS) +
            '.' +
            formatNumber(millisecond, NumberFormats.TWO_DIGITS)
        );
    },

    /**
     * Given hours, minutes, seconds, and milliseconds, formats the string to display a duration.
     * Returns a value like "02:01:25.01". - hours:minutes:seconds.milliseconds
     *
     * @param hours the hours to use.
     * @param minutes the minutes to use.
     * @param seconds the seconds to use.
     * @param milliseconds the milliseconds to use.
     */
    formatDurationHrsMinSecMil: function(hours, minutes, seconds, millisecond) {
        return (
            formatNumber(hours, NumberFormats.TWO_DIGITS) +
            ':' +
            formatNumber(minutes, NumberFormats.TWO_DIGITS) +
            ':' +
            formatNumber(seconds, NumberFormats.TWO_DIGITS) +
            '.' +
            formatNumber(millisecond, NumberFormats.TWO_DIGITS)
        );
    },

    personalizeMTBSpeed(value, activityType, Localizer) {
        if (value === null) {
            return '--';
        } else {
            return (
                this.personalizeSpeed(value, activityType) +
                (Localizer ? ' ' + Localizer.localize(Personalizer.getSpeedUnitKey()) : '')
            );
        }
    },

    personalizeBloodGlucose: function(value, unit, returnDashForZero = false) {
        if (value === 0 && returnDashForZero) {
            return '--';
        }

        if (unit === 'milligramsPerDecilitre' || ('' + value).indexOf('.') === -1) {
            return formatNumber(value, this.numberSeparator, NumberFormats.NO_DECIMAL_NOSEP);
        } else if (unit === 'millimolesPerLitre') {
            return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
        } else {
            return value;
        }
    },

    personalizeBabyMovements: function(value, returnDashForZero = false) {
        if (value === 0 && returnDashForZero) {
            return '--';
        }

        var numberFormat = NumberFormats.PRECISION1;

        if (('' + value).indexOf('.') === -1) {
            numberFormat = NumberFormats.NO_DECIMAL_NOSEP;
        }

        return formatNumber(value, this.numberSeparator, numberFormat);
    },

    getAvgSpeedMphKphLabelKey: function() {
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            return 'label_avg_speed_kph'; // Avg Speed (kph)
        } else {
            return 'label_avg_speed_mph'; // Avg Speed (mph)
        }
    },

    getMaxSpeedMphKphLabelKey: function() {
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            return 'label_max_speed_kph'; // Max Speed (kph)
        } else {
            return 'label_max_speed_mph'; // Max Speed (mph)
        }
    },

    get10sMaxSpeedMphKphLabelKey: function() {
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            return 'label_10s_max_speed_kph'; // Avg Speed (kph)
        } else {
            return 'label_10s_max_speed_mph'; // Avg Speed (mph)
        }
    },

    get2sMaxSpeedMphKphLabelKey: function() {
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            return 'label_2s_max_speed_kph'; // Max Speed (kph)
        } else {
            return 'label_2s_max_speed_mph'; // Max Speed (mph)
        }
    },

    personalizerSpeedWithoutActivityType: function(value) {
        let speed;
        if (this.measurementSystem == this.MeasurementSystems.METRIC) {
            speed = value * this.ConversionFactors.MetersToKm * 3600; // km/hr
        } else {
            speed = value * this.ConversionFactors.MetersToMiles * 3600; // mi/hr
        }
        return this.formatSpeed(speed);
    },

    /**
     * Personalizes average swim strokes.
     *
     * @param value the value to personalize.
     */
    personalizeAvgSwimStrokes: function(value) {
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    /**
     * Goal Time (Pace)
     *
     * @param goalTime goalTime in seconds
     * @param distance distance in meters
     */
    personalizeGoalTimePace(goalTime, distance, i18next) {
        return `${this.personalizeDuration(goalTime)} (${this.personalizePace(distance / goalTime)} ${i18next.t(
            `units:${this.getPaceUnitKey()}`
        )})`;
    },

    /**
     * formats Accute and Chronic load ratio in with 1 decimal.
     * ex 0.8, 1.0, 1.2.
     * @param {number} value
     * @returns
     */
    personalizeLoadRatio: function(value) {
        if (isNull(value) || isUndefined(value)) {
            return '--';
        }
        return formatNumber(value, this.numberSeparator, NumberFormats.PRECISION1);
    },

    getMaxElevationGainValue() {
        if (this.measurementSystem === this.MeasurementSystems.METRIC) {
            return 27432;
        }
        return 90000;
    },

    getBottomTimeLabelKey(activityTypeKey) {
        if (ActivitiesUtil.isApneaDiving(activityTypeKey)) {
            return 'session_dive_time';
        }
        return 'dive_time';
    },

    getMaxDepthLabelKey(activityTypeKey) {
        if (ActivitiesUtil.isApneaDiving(activityTypeKey)) {
            return 'deepest_dive';
        }
        return 'max_depth';
    },

    getMaxBottomTimeLabelKey(activityTypeKey) {
        if (ActivitiesUtil.isApneaDiving(activityTypeKey)) {
            return 'longest_dive_time';
        }
        return 'dive_time';
    },

    isMetricMeasurementSystem() {
        return this.getMeasurementSystem() === this.MeasurementSystems.METRIC;
    },

    // Rounds the first decimal place either up or down, for example, 5.55 can become either 5.6 or 5.5
    // Limit the incoming decimal to two places, after a metric to statute conversion, some values can have lots of decimal places causing 13.0000174 to display as 13.1
    roundDecimal(value, roundUp) {
        /**
         * handle edge case like 1.005, round to 1.01, then finally round up to 1.1
         * in js, 1.005 * 1000 = 1004.9999, so i use double round...
         */
        let value100 = Math.round(Math.round(value * 1000) / 10);
        let integer100 = Math.floor(value) * 100;
        let decimal100 = value100 - integer100;
        let roundedDecimal100 = decimal100;

        if (roundUp) {
            roundedDecimal100 = Math.ceil(decimal100 / 10) * 10;
        } else {
            roundedDecimal100 = Math.floor(decimal100 / 10) * 10;
        }

        return this.formatDecimalNumber((integer100 + roundedDecimal100) / 100, 1);
    },

    getPaceText(durationValue, distanceValue, distanceUnit, activityTypeKey, Localizer, viewerUserPreferences) {
        let mps = 0,
            text = Localizer.localize(
                this.getSpeedTypeKey(activityTypeKey, viewerUserPreferences.getMeasurementSystem())
            );

        if (durationValue > 0 && distanceValue > 0) {
            mps = Conversion.convertToMetersPerSecond(distanceUnit, distanceValue, durationValue);

            let avgPace = this.personalizeSpeed(mps, activityTypeKey, DistanceUnits.meter);

            if (typeof avgPace === 'string' && avgPace.split(':').length > 2) {
                avgPace = null;
            } else if (avgPace === 0) {
                avgPace = null;
            }

            if (avgPace) {
                text =
                    avgPace +
                    ' ' +
                    Localizer.localize(
                        this.getSpeedTypeKey(activityTypeKey, viewerUserPreferences.getMeasurementSystem())
                    );
            }
        }
        return text;
    },

    getDeviceVersionFormatted(versionMajor, versionMinor) {
        if (versionMinor) {
            return `${versionMajor}.${versionMinor.padStart(2, '0')}`;
        } else if (versionMajor) {
            return `${versionMajor}`;
        }

        return false;
    },

    getGamingMatchesOutcomeText(outcomeKey, i18next) {
        if (isNull(outcomeKey) || isUndefined(outcomeKey)) {
            return '--';
        }

        switch (outcomeKey) {
            case 'WIN':
                return i18next.t('activity:label_gaming_outcome_win');
            case 'LOSS':
                return i18next.t('activity:label_gaming_outcome_loss');
            case 'TIE':
                return i18next.t('activity:label_gaming_outcome_tie');
            case 'FORFEIT':
                return i18next.t('activity:label_gaming_outcome_forfeit');
        }
    },

    /**
     * Description: Convert a string that represents a number to an  actual number value, this works for both comma and decimal number separated strings
     *  Examples:
     *      - "123,456.78" -> 123456.78
     *      - "123.456,78" -> 123456.78
     *
     * @param {string} numberStr
     * @returns {number}
     */
    stringToNumber(numberStr) {
        if (this.numberSeparator === NumberSeparators.DECIMAL_COMMA) {
            return +numberStr.replaceAll('.', '').replaceAll(',', '.');
        } else {
            return +numberStr.replaceAll(',', '');
        }
    },

    convertMetersToCm(value) {
        return value * this.ConversionFactors.MetersToCm;
    }
};

Personalizer.MeasurementSystems = MeasurementSystems;
Personalizer.SpeedTypes = SpeedTypes;
Personalizer.ConversionFactors = ConversionFactors;
Personalizer.NumberFormats = NumberFormats;
Personalizer.NumberSeparator = NumberSeparators;
Personalizer.DateFormats = DateFormats;
Personalizer.WeightUnitKeys = WeightUnitKeys;
Personalizer.CadenceSensorTypes = CadenceSensorTypes;

export default Personalizer;
